import React, { useState, useEffect, useRef } from 'react';
import { Capacitor } from '@capacitor/core';
import ApiService from '../services/api';
import SOCSSetupModal from './SOCSSetupModal';
import PitcheroSetupModal from './PitcheroSetupModal';
import SchoolsBuddySetupModal from './SchoolsBuddySetupModal';
import SchoolPortalSetupModal from './SchoolPortalSetupModal';
import SpondSetupModal from './SpondSetupModal';
import TermPlannerClubModal from './TermPlannerClubModal';
import TimePickerDropdown from './TimePickerDropdown';
import './OnboardingFlow.css';

interface TermPlannerInfo {
  childUuid: string;
  childName: string;
  termPlannerUuid: string;
}

interface OnboardingFlowProps {
  onComplete: () => void;
  onNavigateToProfile: () => void;
  onNavigateToChildren?: () => void;
  onNavigateToConnections?: () => void;
  onParentAdded?: () => void;
  connectionsWaitingCount?: number;
  onNavigateToTermPlanner?: (termPlanners: TermPlannerInfo[], childrenWithoutPlanners: any[]) => void;
}

interface SkeletonChild {
  uuid: string;
  first_name: string;
  last_name: string;
  birth_date?: string;
  age?: number;
}

// App categories for per-child questionnaire
const APP_CATEGORIES = [
  {
    key: 'sportsAndSocial' as const,
    title: (childName: string) => `What sports and social apps do you use for ${childName}?`,
    subtitle: 'Select any apps used for sports clubs and social activities',
    apps: [
      { id: 'pitchero', name: 'Pitchero', description: 'Most popular in UK rugby, football, cricket & hockey', icon: '🏟️' },
      { id: 'spond', name: 'Spond', description: 'Team communication and scheduling', icon: '📱' },
      { id: 'teamo', name: 'Teamo', description: 'Popular team management platform', icon: '👥', comingSoon: true },
      { id: 'teamsnap', name: 'TeamSnap', description: 'Popular in North America', icon: '📊', comingSoon: true },
    ]
  },
  {
    key: 'schoolEvents' as const,
    title: (childName: string) => `What app does ${childName}'s school use for school events?`,
    subtitle: 'Select the app your school uses to tell you about events',
    apps: [
      { id: 'schoolportal', name: 'My School Portal', description: 'Import events from your school calendar', icon: '🗓️' },
    ]
  },
  {
    key: 'sportingFixtures' as const,
    title: (childName: string) => `What app does ${childName}'s school use for sporting fixtures?`,
    subtitle: 'Select the app used for school sport fixtures',
    apps: [
      { id: 'socs', name: 'SOCS', description: 'School sport fixture management', icon: '🏫' },
      { id: 'schoolportal', name: 'My School Portal', description: 'Import events from your school calendar', icon: '🗓️' },
    ]
  },
  {
    key: 'clubsAndExtraCurricular' as const,
    title: (childName: string) => `What app does ${childName}'s school use for clubs, extra-curricular lessons, music and wrap around care?`,
    subtitle: 'Select the app used for clubs and activities',
    apps: [
      { id: 'schoolsbuddy', name: 'SchoolsBuddy', description: 'Import clubs and activities from your school', icon: '📚' },
      { id: 'schoolportal', name: 'My School Portal', description: 'Import events from your school calendar', icon: '🗓️' },
    ]
  }
];

type AppCategoryKey = 'sportsAndSocial' | 'schoolEvents' | 'sportingFixtures' | 'clubsAndExtraCurricular';

// Define the step order for navigation
const STEP_ORDER: Array<'welcome' | 'add_children_prompt' | 'child_setup' | 'skeleton_dob' | 'child_app_questionnaire' | 'integrations_setup' | 'pitchero_mappings_review' | 'term_planner_intro' | 'term_planner_setup' | 'connections' | 'add_to_home_screen' | 'widget_setup'> = [
  'welcome',
  'add_children_prompt',
  'child_setup',
  'child_app_questionnaire',
  'integrations_setup',
  'connections',
  'add_to_home_screen',
  'widget_setup'
];

const STEP_LABELS: { [key: string]: string } = {
  'welcome': 'Welcome',
  'add_children_prompt': 'Add Children',
  'child_setup': 'Children',
  'skeleton_dob': 'Child Details',
  'child_app_questionnaire': 'App Setup',
  'integrations_setup': 'Integrations',
  'pitchero_mappings_review': 'Pitchero Mappings',
  'term_planner_intro': 'Term Planner',  // kept for type compat
  'term_planner_setup': 'Term Details',  // kept for type compat
  'connections': 'Connect',
  'add_to_home_screen': 'Install App',
  'widget_setup': 'Widget'
};

/** UUID of the account currently signed in, or null. */
const getCurrentUserUuid = (): string | null => {
  try {
    const raw = localStorage.getItem('userData');
    return raw ? (JSON.parse(raw)?.uuid || null) : null;
  } catch {
    return null;
  }
};

/**
 * The saved onboarding step, but only if it was saved by the account that is
 * signed in now. A step left behind by a different account is discarded (and
 * cleaned up) rather than replayed against someone else's data.
 */
const getSavedStepForCurrentUser = (): string | null => {
  const step = localStorage.getItem('onboarding_current_step');
  if (!step) return null;

  const owner = localStorage.getItem('onboarding_step_user');
  const current = getCurrentUserUuid();

  // No recorded owner means the step predates this check - only trust it when
  // we cannot tell who is signed in, otherwise treat it as another account's.
  if (owner && current && owner === current) return step;
  if (!owner && !current) return step;

  localStorage.removeItem('onboarding_current_step');
  localStorage.removeItem('onboarding_step_user');
  return null;
};

const OnboardingFlow: React.FC<OnboardingFlowProps> = ({ onComplete, onNavigateToProfile, onNavigateToChildren, onNavigateToConnections, onParentAdded, connectionsWaitingCount = 0, onNavigateToTermPlanner }) => {
  const [userInfo, setUserInfo] = useState<any>(null);
  const [parentCount, setParentCount] = useState<number>(0);
  const [loading, setLoading] = useState(true);
  // Restore step from localStorage if available (for when returning from profile/children screens).
  // The saved step belongs to ONE account: logging out and registering a new one
  // only clears authToken/userData, so without this check a fresh signup inherits
  // the previous account's step and lands on a screen it has no data for.
  const savedStep = getSavedStepForCurrentUser();
  const initialStep = (savedStep && ['welcome', 'add_children_prompt', 'child_app_questionnaire', 'connections', 'add_to_home_screen', 'widget_setup'].includes(savedStep))
    ? savedStep as 'welcome' | 'add_children_prompt' | 'child_app_questionnaire' | 'connections' | 'add_to_home_screen' | 'widget_setup'
    : 'welcome';
  const [currentStep, setCurrentStep] = useState<'welcome' | 'add_children_prompt' | 'parent_added' | 'child_setup' | 'skeleton_dob' | 'child_app_questionnaire' | 'integrations_setup' | 'pitchero_mappings_review' | 'term_planner_intro' | 'term_planner_setup' | 'connections' | 'add_to_home_screen' | 'widget_setup'>(initialStep);
  const [stepHistory, setStepHistory] = useState<string[]>(['welcome']); // Track visited steps for back navigation
  const [isNavigatingBack, setIsNavigatingBack] = useState(false); // Flag to prevent adding to history during back navigation
  const [deferredPrompt, setDeferredPrompt] = useState<any>(null);
  const [isIOS, setIsIOS] = useState(false);
  const [isIOSNative, setIsIOSNative] = useState(false);
  const [isStandalone, setIsStandalone] = useState(false);
  const [skeletonChildren, setSkeletonChildren] = useState<SkeletonChild[]>([]);
  const [allChildren, setAllChildren] = useState<any[]>([]); // Full children data for school info
  const [selectedSkeletonChild, setSelectedSkeletonChild] = useState<SkeletonChild | null>(null);
  const [childBirthDate, setChildBirthDate] = useState('');
  const [childFirstName, setChildFirstName] = useState('');
  const [childLastName, setChildLastName] = useState('');
  const [updatingChild, setUpdatingChild] = useState(false);
  const [showWelcomeForParentAdded, setShowWelcomeForParentAdded] = useState(false);
  const [childrenCount, setChildrenCount] = useState<number>(0);
  const parentAddedRef = useRef(false);

  // Club apps selection state
  const [selectedClubApps, setSelectedClubApps] = useState<string[]>([]);
  const [existingIntegrations, setExistingIntegrations] = useState<{[key: string]: boolean}>({});

  // Per-child app questionnaire state
  const [childAppSelections, setChildAppSelections] = useState<{[childUuid: string]: {
    sportsAndSocial: string[];
    schoolEvents: string[];
    sportingFixtures: string[];
    clubsAndExtraCurricular: string[];
  }}>({});
  const [currentQuestionnaireChildIndex, setCurrentQuestionnaireChildIndex] = useState(0);
  const [currentQuestionCategory, setCurrentQuestionCategory] = useState(0);
  const [showChildIntroScreen, setShowChildIntroScreen] = useState(true); // Show intro before each child's questions
  const [showIntegrationsIntro, setShowIntegrationsIntro] = useState(true); // Show intro before launching integration modals
  const [showSchoolsBuddyIntro, setShowSchoolsBuddyIntro] = useState(false); // Show intro before SchoolsBuddy modal
  const [schoolsBuddyChildIndex, setSchoolsBuddyChildIndex] = useState(0); // Track which child SchoolsBuddy is processing
  const [schoolsBuddyChildrenList, setSchoolsBuddyChildrenList] = useState<any[]>([]); // Children to process through SchoolsBuddy

  // Integrations setup state
  const [pitcheroUsername, setPitcheroUsername] = useState('');
  const [pitcheroPassword, setPitcheroPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [connectingIntegration, setConnectingIntegration] = useState(false);
  const [primaryParentUuid, setPrimaryParentUuid] = useState<string | null>(null);

  // Pitchero mappings review state
  const [pitcheroMappings, setPitcheroMappings] = useState<any[]>([]);
  const [availableChildren, setAvailableChildren] = useState<any[]>([]);
  const [savingMappings, setSavingMappings] = useState(false);

  // SOCS setup state
  const [showSOCSModal, setShowSOCSModal] = useState(false);
  const [socsCompleted, setSocsCompleted] = useState(false);
  const [showSOCSCompletionScreen, setShowSOCSCompletionScreen] = useState(false);
  const [childrenForSOCS, setChildrenForSOCS] = useState<any[]>([]);

  // Pitchero setup state (modal-based)
  const [showPitcheroModal, setShowPitcheroModal] = useState(false);
  const [pitcheroCompleted, setPitcheroCompleted] = useState(false);
  const [showPitcheroCompletionScreen, setShowPitcheroCompletionScreen] = useState(false);

  // SchoolsBuddy setup state (modal-based)
  const [showSchoolsBuddyModal, setShowSchoolsBuddyModal] = useState(false);
  const [showSchoolsBuddyCompletionScreen, setShowSchoolsBuddyCompletionScreen] = useState(false);

  // School Portal setup state (modal-based)
  const [showSchoolPortalModal, setShowSchoolPortalModal] = useState(false);
  const [schoolPortalCompleted, setSchoolPortalCompleted] = useState(false);
  const [showSchoolPortalCompletionScreen, setShowSchoolPortalCompletionScreen] = useState(false);

  // Spond setup state (modal-based)
  const [showSpondModal, setShowSpondModal] = useState(false);
  const [spondCompleted, setSpondCompleted] = useState(false);
  const [showSpondCompletionScreen, setShowSpondCompletionScreen] = useState(false);

  // Add Child modal state
  const [showAddChildModal, setShowAddChildModal] = useState(false);
  const [newChildFirstName, setNewChildFirstName] = useState('');
  const [newChildLastName, setNewChildLastName] = useState('');
  const [newChildBirthDay, setNewChildBirthDay] = useState('');
  const [newChildBirthMonth, setNewChildBirthMonth] = useState('');
  const [newChildBirthYear, setNewChildBirthYear] = useState('');
  const [newChildSchoolVenue, setNewChildSchoolVenue] = useState('');
  const [savingChild, setSavingChild] = useState(false);

  // Add Parent modal state
  const [showAddParentModal, setShowAddParentModal] = useState(false);
  const [newParentFirstName, setNewParentFirstName] = useState('');
  const [newParentLastName, setNewParentLastName] = useState('');
  const [newParentEmail, setNewParentEmail] = useState('');
  const [newParentPhone, setNewParentPhone] = useState('');
  const [newParentPassword, setNewParentPassword] = useState('');
  const [newParentConfirmPassword, setNewParentConfirmPassword] = useState('');
  const [newParentRole, setNewParentRole] = useState('parent');
  const [showParentPassword, setShowParentPassword] = useState(false);
  const [showParentConfirmPassword, setShowParentConfirmPassword] = useState(false);
  const [addingParent, setAddingParent] = useState(false);
  const [allParents, setAllParents] = useState<any[]>([]);

  // Custom emails entered during integration setup (for reuse across modals)
  const [customEmails, setCustomEmails] = useState<string[]>([]);

  // SchoolsBuddy setup state
  const [schoolsBuddyEmail, setSchoolsBuddyEmail] = useState('');
  const [schoolsBuddyPassword, setSchoolsBuddyPassword] = useState('');
  const [schoolsBuddySchoolName, setSchoolsBuddySchoolName] = useState('');
  const [schoolsBuddySchoolUrl, setSchoolsBuddySchoolUrl] = useState('');
  const [showSchoolsBuddyPassword, setShowSchoolsBuddyPassword] = useState(false);
  const [searchingSchools, setSearchingSchools] = useState(false);
  const [schoolSearchResults, setSchoolSearchResults] = useState<any[]>([]);
  const [selectedSchoolsBuddySite, setSelectedSchoolsBuddySite] = useState('');
  const [connectingSchoolsBuddy, setConnectingSchoolsBuddy] = useState(false);
  const [schoolsBuddyCompleted, setSchoolsBuddyCompleted] = useState(false);
  const [schoolsBuddyAutoSearchTriggered, setSchoolsBuddyAutoSearchTriggered] = useState(false);
  const [showSchoolSearchExpanded, setShowSchoolSearchExpanded] = useState(false);

  const [transitioningToTermPlanner, setTransitioningToTermPlanner] = useState(false);

  // Term planner state
  const [termPlannerChildren, setTermPlannerChildren] = useState<any[]>([]);
  const [termPlannerParents, setTermPlannerParents] = useState<any[]>([]);
  const [selectedTermChild, setSelectedTermChild] = useState<string>('');
  const [termName, setTermName] = useState('');
  const [termStartDate, setTermStartDate] = useState('');
  const [termEndDate, setTermEndDate] = useState('');
  const [defaultSchoolStartTime, setDefaultSchoolStartTime] = useState('08:45');
  const [defaultSchoolEndTime, setDefaultSchoolEndTime] = useState('15:30');
  const [childrenWithTermPlanners, setChildrenWithTermPlanners] = useState<Set<string>>(new Set());
  const [currentDayTab, setCurrentDayTab] = useState(0); // 0=Monday, 1=Tuesday, etc.
  const [dailySchedules, setDailySchedules] = useState<any>({
    0: { drop_off: {}, club_reminders: [], pick_up: {} },
    1: { drop_off: {}, club_reminders: [], pick_up: {} },
    2: { drop_off: {}, club_reminders: [], pick_up: {} },
    3: { drop_off: {}, club_reminders: [], pick_up: {} },
    4: { drop_off: {}, club_reminders: [], pick_up: {} },
    5: { drop_off: {}, club_reminders: [], pick_up: {} },
    6: { drop_off: {}, club_reminders: [], pick_up: {} }
  });
  // Store form data per child
  const [childFormData, setChildFormData] = useState<any>({});
  const [showClubModal, setShowClubModal] = useState(false);
  const [clubModalType, setClubModalType] = useState<'drop_off' | 'pick_up' | 'club_reminder'>('drop_off');
  const [currentClubReminderIndex, setCurrentClubReminderIndex] = useState<number>(0);
  const dailyScheduleRef = useRef<HTMLDivElement>(null);
  const termPlannerContainerRef = useRef<HTMLDivElement>(null);

  const apiService = ApiService.getInstance();

  // Track step changes and add to history (unless navigating back)
  // Also save to localStorage for persistence across navigation
  useEffect(() => {
    // Save current step to localStorage for restoration after navigation, stamped
    // with the account it belongs to so another account can't pick it up.
    localStorage.setItem('onboarding_current_step', currentStep);
    const uuid = getCurrentUserUuid();
    if (uuid) localStorage.setItem('onboarding_step_user', uuid);

    if (isNavigatingBack) {
      setIsNavigatingBack(false);
      return;
    }
    // Only add to history if it's a new step (not already the last one)
    setStepHistory(prev => {
      if (prev[prev.length - 1] !== currentStep) {
        return [...prev, currentStep];
      }
      return prev;
    });
  }, [currentStep, isNavigatingBack]);

  // Navigation helper functions
  const goToPreviousStep = () => {
    if (stepHistory.length > 1) {
      // Remove current step from history
      const newHistory = stepHistory.slice(0, -1);
      const previousStep = newHistory[newHistory.length - 1] as typeof currentStep;
      setIsNavigatingBack(true);
      setStepHistory(newHistory);
      setCurrentStep(previousStep);
    }
  };

  // Complete onboarding and clear localStorage
  const completeOnboarding = () => {
    localStorage.removeItem('onboarding_current_step');
    localStorage.removeItem('onboarding_step_user');
    onComplete();
  };

  const goToNextStep = async () => {
    // Use the existing skip/continue logic for each step
    switch (currentStep) {
      case 'welcome':
        // Skip add parent - go to add children prompt
        setCurrentStep('add_children_prompt');
        break;
      case 'add_children_prompt':
        // Skip to club apps selection
        setCurrentStep('child_app_questionnaire');
        break;
      case 'child_setup':
      case 'skeleton_dob':
        // Skip child setup - go to club apps
        setCurrentStep('child_app_questionnaire');
        break;
      case 'child_app_questionnaire':
        // Skip/Next pressed - aggregate whatever is selected so far and proceed
        await handleContinueFromQuestionnaire();
        break;
      case 'integrations_setup':
        // Check if a completion screen is showing - if so, move to next integration
        if (showPitcheroCompletionScreen || showSpondCompletionScreen || showSOCSCompletionScreen || showSchoolPortalCompletionScreen || showSchoolsBuddyCompletionScreen) {
          handleContinueFromCompletionScreen();
          return;
        }
        // Integrations are now handled via modals that auto-open
        // The "Next" button just moves to term planner (skipping remaining integrations)
        await handleMoveToTermPlanner();
        break;
      case 'pitchero_mappings_review':
        // Start the import process (same as Approve & Import button)
        await handleContinueFromMappings();
        break;
      case 'term_planner_intro':
      case 'term_planner_setup':
        // These steps are no longer used — TP flow is handled by ProfileScreen
        setCurrentStep('connections');
        break;
      case 'connections':
        // On iOS native, skip add_to_home_screen (web-only) and go to widget_setup
        if (isIOSNative) {
          setCurrentStep('widget_setup');
        } else {
          setCurrentStep('add_to_home_screen');
        }
        break;
      case 'add_to_home_screen':
        // On iOS native, show widget setup step; otherwise complete onboarding
        if (isIOSNative) {
          setCurrentStep('widget_setup');
        } else {
          completeOnboarding();
        }
        break;
      case 'widget_setup':
        // Complete onboarding
        completeOnboarding();
        break;
      default:
        completeOnboarding();
    }
  };

  const canGoBack = () => {
    return stepHistory.length > 1;
  };

  const canGoForward = () => {
    // Always allow skip/forward except we show "Finish" on last step
    // On iOS native, widget_setup is the last step; otherwise add_to_home_screen
    if (isIOSNative) {
      return currentStep !== 'widget_setup';
    }
    return currentStep !== 'add_to_home_screen';
  };

  const getCurrentStepNumber = () => {
    const currentIndex = STEP_ORDER.indexOf(currentStep as any);
    return currentIndex >= 0 ? currentIndex + 1 : 1;
  };

  useEffect(() => {
    const loadData = async () => {
      try {
        // Get current user info for personalization
        const userData = localStorage.getItem('userData');
        if (userData) {
          const parsedUserData = JSON.parse(userData);
          setUserInfo(parsedUserData);

          // Check if this is a skeleton account conversion (not a second parent!)
          if (parsedUserData.skeletonMerge && parsedUserData.skeletonMerge.hasConnectionsWaiting) {
            // Set a flag to show appropriate welcome message later, but don't set step yet
            // We need to check parent count first to determine the correct flow
          }
        }

        // Load parents and children in parallel for better performance
        const [parentsResponse, childrenResponse] = await Promise.all([
          apiService.getParents(),
          apiService.getChildren()
        ]);

        // Process parents response
        if (parentsResponse.success && parentsResponse.data) {
          const parents = parentsResponse.data;
          setParentCount(parents.length);
          setAllParents(parents); // Store full parents data for display

          // Get primary parent UUID for integrations setup
          const primaryParent = parents.find((p: any) => p.is_primary) || parents[0];
          if (primaryParent) {
            setPrimaryParentUuid(primaryParent.uuid);
          }

          const userData = localStorage.getItem('userData');
          const parsedUserData = userData ? JSON.parse(userData) : null;
          const hasSkeletonMerge = parsedUserData?.skeletonMerge?.hasConnectionsWaiting;

          // Only auto-complete on initial load, not when parent added event has been triggered
          // Check localStorage flag, state, and ref to catch parent added flow
          const parentJustAdded = localStorage.getItem('parentJustAdded') === 'true';
          const isParentAddedFlow = showWelcomeForParentAdded || parentAddedRef.current || parentJustAdded;

          // Check if we're restoring from localStorage (user navigated away and back)
          const restoringFromStorage = localStorage.getItem('onboarding_current_step') !== null;

          // Determine the correct flow based on parent count and skeleton merge status
          // Skip auto-complete if restoring from localStorage (user is mid-flow)
          if (hasSkeletonMerge) {
            // Skeleton account converted to real account - show skeleton welcome message
            if (!restoringFromStorage) {
              setCurrentStep('welcome'); // Show normal welcome with skeleton context
            }
          } else if (parents.length >= 2 && !isParentAddedFlow && !restoringFromStorage) {
            // Multiple parents exist and no special flow needed
            // Only auto-complete if not restoring from a saved step
            completeOnboarding();
            return;
          }
          // For single parent OR when parent was just added via onboarding flow, stay on current step
          // This allows the primary parent to add multiple parents or continue to add children
        }

        // Process children response
        if (childrenResponse.success && childrenResponse.data) {
          const children = childrenResponse.data;
          setChildrenCount(children.filter((c: any) => !c.is_parent).length);
          setAllChildren(children);
          const skeletonChildrenMissingDOB = children.filter((child: any) =>
            !child.birth_date && child.is_skeleton
          );

          setSkeletonChildren(skeletonChildrenMissingDOB);

          // If there are skeleton children that need completion, go directly to child_setup step
          const userData = localStorage.getItem('userData');
          const parsedUserData = userData ? JSON.parse(userData) : null;
          const hasSkeletonMerge = parsedUserData?.skeletonMerge?.hasConnectionsWaiting;

          // Check if we're restoring from localStorage (user navigated away and back)
          const restoringFromStorage = localStorage.getItem('onboarding_current_step') !== null;

          if (hasSkeletonMerge && skeletonChildrenMissingDOB.length > 0 && !restoringFromStorage) {
            console.log('🔵 OnboardingFlow: Skeleton children need completion, setting step to child_setup');
            setCurrentStep('child_setup');
          }
          // Note: We no longer auto-skip to child_app_questionnaire when children exist.
          // Users should go through add_children_prompt to add more children or continue.
        }

        // Load existing integrations to check what's already configured in the account
        await loadExistingIntegrations();
      } catch (error) {
        console.error('Failed to load onboarding data:', error);
      } finally {
        setLoading(false);
      }
    };

    loadData();

    // Listen for parent added event
    const handleParentAddedEvent = () => {
      // Set both flag and ref to show welcome modal even with multiple parents
      setShowWelcomeForParentAdded(true);
      parentAddedRef.current = true;
      // Clear localStorage flag if it exists
      localStorage.removeItem('parentJustAdded');
      // Reset to welcome step when parent is added
      setCurrentStep('welcome');
      if (onParentAdded) {
        onParentAdded();
      }
    };

    // Listen for child creation to update count
    const handleChildCreated = async () => {
      const childrenResponse = await apiService.getChildren();
      if (childrenResponse.success && childrenResponse.data) {
        setChildrenCount(childrenResponse.data.length);
      }
    };

    window.addEventListener('parentAdded', handleParentAddedEvent);
    window.addEventListener('childCreated', handleChildCreated);

    return () => {
      window.removeEventListener('parentAdded', handleParentAddedEvent);
      window.removeEventListener('childCreated', handleChildCreated);
    };
  }, [onComplete, onParentAdded]);

  // Detect iOS and PWA install prompt
  useEffect(() => {
    // Detect if running on iOS
    const userAgent = window.navigator.userAgent.toLowerCase();
    const ios = /iphone|ipad|ipod/.test(userAgent);
    setIsIOS(ios);

    // Detect if running as iOS native app (Capacitor)
    const iosNative = Capacitor.getPlatform() === 'ios';
    setIsIOSNative(iosNative);

    // Check if already installed as PWA
    const standalone = window.matchMedia('(display-mode: standalone)').matches || (window.navigator as any).standalone;
    setIsStandalone(standalone);

    // Listen for install prompt (Android/Chrome)
    const handleBeforeInstallPrompt = (e: any) => {
      e.preventDefault();
      setDeferredPrompt(e);
    };

    window.addEventListener('beforeinstallprompt', handleBeforeInstallPrompt);

    return () => {
      window.removeEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
    };
  }, []);

  // Auto-open integration modals in sequence when on integrations_setup step
  useEffect(() => {
    if (currentStep === 'integrations_setup') {
      // Don't auto-open modals while showing the intro screen
      if (showIntegrationsIntro) return;

      const hasPitchero = selectedClubApps.includes('pitchero');
      const hasSpond = selectedClubApps.includes('spond');
      const hasSocs = selectedClubApps.includes('socs');
      const hasSchoolPortal = selectedClubApps.includes('schoolportal');
      const hasSchoolsBuddy = selectedClubApps.includes('schoolsbuddy');

      console.log('🔍 Integration useEffect - selectedClubApps:', selectedClubApps);
      console.log('🔍 Integration useEffect - has:', { hasPitchero, hasSpond, hasSocs, hasSchoolPortal, hasSchoolsBuddy });
      console.log('🔍 Integration useEffect - completed:', { pitcheroCompleted, spondCompleted, socsCompleted, schoolPortalCompleted, schoolsBuddyCompleted });

      // If any completion screen is showing, don't auto-advance
      if (showPitcheroCompletionScreen || showSpondCompletionScreen || showSOCSCompletionScreen || showSchoolPortalCompletionScreen || showSchoolsBuddyCompletionScreen) {
        return;
      }

      // Pitchero first - but wait for primaryParentUuid to be loaded
      if (hasPitchero && !pitcheroCompleted && !showPitcheroModal) {
        if (primaryParentUuid) {
          setShowPitcheroModal(true);
        }
        // Don't continue until Pitchero is shown or completed
        return;
      }

      // Spond second (after Pitchero completes or if not selected)
      if (hasSpond && !spondCompleted && !showSpondModal && (!hasPitchero || pitcheroCompleted)) {
        setShowSpondModal(true);
        return;
      }

      // SOCS third (after Spond completes or if not selected)
      if (hasSocs && !socsCompleted && !showSOCSModal &&
          (!hasPitchero || pitcheroCompleted) && (!hasSpond || spondCompleted)) {
        handleOpenSOCSModal();
        return;
      }

      // School Portal fourth (after SOCS completes or if not selected)
      if (hasSchoolPortal && !schoolPortalCompleted && !showSchoolPortalModal &&
          (!hasPitchero || pitcheroCompleted) && (!hasSpond || spondCompleted) && (!hasSocs || socsCompleted)) {
        setShowSchoolPortalModal(true);
        return;
      }

      // SchoolsBuddy last (after School Portal completes or if not selected).
      // No pre-modal intro screen — the "Let's set up X's activities" intro is
      // owned by the term-planner flow (ProfileScreen) and fires before every
      // TP editor opens, regardless of which integration populated the data.
      if (hasSchoolsBuddy && !schoolsBuddyCompleted && !showSchoolsBuddyModal &&
          (!hasPitchero || pitcheroCompleted) && (!hasSpond || spondCompleted) && (!hasSocs || socsCompleted) && (!hasSchoolPortal || schoolPortalCompleted)) {
        // Populate the per-child list so the modal only shows children
        // the user actually selected SchoolsBuddy for in the questionnaire
        if (schoolsBuddyChildrenList.length === 0) {
          const sbChildren = getChildrenForApp('schoolsbuddy');
          if (sbChildren.length > 0) {
            setSchoolsBuddyChildrenList(sbChildren);
          }
          setSchoolsBuddyChildIndex(0);
        }
        setShowSchoolsBuddyModal(true);
        return;
      }

      // All selected integrations complete - move to next step
      const allComplete = (!hasPitchero || pitcheroCompleted) &&
                          (!hasSpond || spondCompleted) &&
                          (!hasSocs || socsCompleted) &&
                          (!hasSchoolPortal || schoolPortalCompleted) &&
                          (!hasSchoolsBuddy || schoolsBuddyCompleted);
      if (allComplete && (hasPitchero || hasSpond || hasSocs || hasSchoolPortal || hasSchoolsBuddy)) {
        // Move to term planner
        handleMoveToTermPlanner();
      }
    }
  }, [currentStep, selectedClubApps, showPitcheroModal, pitcheroCompleted, showSpondModal, spondCompleted, showSOCSModal, socsCompleted, showSchoolPortalModal, schoolPortalCompleted, showSchoolsBuddyModal, schoolsBuddyCompleted, primaryParentUuid, showPitcheroCompletionScreen, showSpondCompletionScreen, showSOCSCompletionScreen, showSchoolPortalCompletionScreen, showSchoolsBuddyCompletionScreen, showIntegrationsIntro, showSchoolsBuddyIntro]);

  // NOTE: SchoolsBuddy auto-search is now handled by SchoolsBuddySetupModal

  const handleAddParent = () => {
    console.log('🔵 OnboardingFlow: handleAddParent clicked - opening modal');
    setShowAddParentModal(true);
  };

  const handleCreateParent = async () => {
    if (!newParentFirstName.trim() || !newParentLastName.trim() || !newParentEmail.trim() || !newParentPhone.trim() || !newParentPassword.trim()) {
      alert('Please fill in all fields');
      return;
    }

    // Validate password match
    if (newParentPassword !== newParentConfirmPassword) {
      alert('Passwords do not match');
      return;
    }

    // Validate password length
    if (newParentPassword.length < 6) {
      alert('Password must be at least 6 characters long');
      return;
    }

    // Validate email format
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(newParentEmail)) {
      alert('Please enter a valid email address');
      return;
    }

    setAddingParent(true);
    try {
      const response = await apiService.createParent({
        username: `${newParentFirstName.trim()} ${newParentLastName.trim()}`,
        email: newParentEmail.trim(),
        phone: newParentPhone.trim(),
        password: newParentPassword,
        role: newParentRole
      });

      if (response.success) {
        // Optimistic update: immediately add to local state so it appears instantly
        const newParent = response.data || {
          uuid: `temp-${Date.now()}`,
          username: `${newParentFirstName.trim()} ${newParentLastName.trim()}`,
          email: newParentEmail.trim(),
          role: newParentRole,
          is_primary: false
        };
        setAllParents(prev => [...prev, newParent]);
        setParentCount(prev => prev + 1);

        // Clear form and close modal
        setNewParentFirstName('');
        setNewParentLastName('');
        setNewParentEmail('');
        setNewParentPhone('');
        setNewParentPassword('');
        setNewParentConfirmPassword('');
        setNewParentRole('parent');
        setShowAddParentModal(false);

        // Set flag to prevent auto-completion when parent count changes
        localStorage.setItem('parentJustAdded', 'true');

        // Background refresh to get server-confirmed data
        apiService.getParents().then(parentsResponse => {
          if (parentsResponse.success && parentsResponse.data) {
            setAllParents(parentsResponse.data);
            setParentCount(parentsResponse.data.length);
          }
        });

        // Dispatch event for other components
        window.dispatchEvent(new CustomEvent('parentAdded'));
      } else {
        alert(`Failed to add parent: ${response.error || 'Unknown error'}`);
      }
    } catch (error) {
      alert('Failed to add parent');
      console.error('Add parent error:', error);
    } finally {
      setAddingParent(false);
    }
  };

  const handleContinueSolo = () => {
    // Children are already loaded in useEffect, no need to fetch again
    // Check if there are skeleton children that need setup
    if (skeletonChildren.length > 0) {
      setCurrentStep('child_setup');
    } else {
      // No skeleton children - open the add child modal
      setShowAddChildModal(true);
    }
  };

  const handleAddChild = async () => {
    if (!newChildFirstName.trim()) {
      alert('Please enter the child\'s first name');
      return;
    }

    if (!newChildSchoolVenue.trim()) {
      alert('Please enter the child\'s school name');
      return;
    }

    setSavingChild(true);
    try {
      let age = undefined;
      // Construct birth date from day/month/year dropdowns
      if (newChildBirthDay && newChildBirthMonth && newChildBirthYear) {
        const birthDate = new Date(
          parseInt(newChildBirthYear),
          parseInt(newChildBirthMonth) - 1,
          parseInt(newChildBirthDay)
        );
        const today = new Date();
        age = today.getFullYear() - birthDate.getFullYear();
        const monthDiff = today.getMonth() - birthDate.getMonth();
        if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birthDate.getDate())) {
          age--;
        }
      }

      const childData = {
        first_name: newChildFirstName.trim(),
        last_name: newChildLastName.trim(),
        age: age,
        school_venue: newChildSchoolVenue.trim() || undefined
      };

      const response = await apiService.createChild(childData);

      if (response.success) {
        // Optimistic update: immediately add to local state so it appears instantly
        const newChild = {
          uuid: response.data?.uuid || `temp-${Date.now()}`,
          first_name: newChildFirstName.trim(),
          last_name: newChildLastName.trim(),
          display_name: `${newChildFirstName.trim()} ${newChildLastName.trim()}`.trim(),
          name: `${newChildFirstName.trim()} ${newChildLastName.trim()}`.trim(),
          age: age,
          school_venue: newChildSchoolVenue.trim() || undefined,
          is_parent: false,
          ...response.data
        };
        setAllChildren(prev => [...prev, newChild]);
        setChildrenCount(prev => prev + 1);

        // Clear form
        setNewChildFirstName('');
        setNewChildLastName('');
        setNewChildBirthDay('');
        setNewChildBirthMonth('');
        setNewChildBirthYear('');
        setNewChildSchoolVenue('');
        setShowAddChildModal(false);

        // Background refresh to get server-confirmed data (with real UUID etc.)
        apiService.getChildren().then(childrenResponse => {
          if (childrenResponse.success && childrenResponse.data) {
            setChildrenCount(childrenResponse.data.filter((c: any) => !c.is_parent).length);
            setAllChildren(childrenResponse.data);
          }
        });

        // Dispatch event for other components
        window.dispatchEvent(new CustomEvent('childrenDataChanged'));
      } else {
        alert(`Error: ${response.error || 'Failed to add child'}`);
      }
    } catch (error) {
      alert('Failed to add child');
      console.error('Add child error:', error);
    } finally {
      setSavingChild(false);
    }
  };

  const handleParentAdded = () => {
    // This is called when a second parent has been successfully added
    // Check if there are skeleton children missing DOB
    if (skeletonChildren.length > 0) {
      setCurrentStep('child_setup');
    } else {
      // No skeleton children - navigate to children screen to add children
      if (onNavigateToChildren) {
        onNavigateToChildren();
      }
    }
  };

  const handleSkipChildSetup = () => {
    // After skipping child setup, show club apps selection
    setCurrentStep('child_app_questionnaire');
  };

  const handleConnectPitchero = async () => {
    if (!primaryParentUuid || !pitcheroUsername || !pitcheroPassword) {
      alert('Please enter both username and password');
      return;
    }

    try {
      setConnectingIntegration(true);

      const response = await apiService.savePitcheroCredentials(primaryParentUuid, {
        username: pitcheroUsername,
        password: pitcheroPassword
      });

      if (response.success) {
        // Trigger match-only sync (no activities yet)
        const syncResponse = await apiService.triggerPitcheroSync(primaryParentUuid, true);

        if (syncResponse.success) {
          // Check if sync failed or no children were found
          const syncFailed = syncResponse.data?.sync_status === 'failed';
          const noChildrenFound = (syncResponse.data?.childrenMatched || 0) === 0 &&
                                  (syncResponse.data?.clubsDiscovered || 0) === 0 &&
                                  (!syncResponse.data?.unmatchedChildren || syncResponse.data.unmatchedChildren.length === 0);

          if (syncFailed || noChildrenFound) {
            // No children found - credentials likely wrong, stay on this screen to re-enter
            alert('❌ No children found!\n\nNo clubs or children were found in your Pitchero account.\n\nThis usually means the email or password is incorrect. Please check your credentials and try again.');
            // Clear the input fields so user can re-enter
            setPitcheroUsername('');
            setPitcheroPassword('');
            return; // Stay on current screen
          }

          // Load mappings to review
          const mappingsResponse = await apiService.getPitcheroMappings(primaryParentUuid);

          if (mappingsResponse.success && mappingsResponse.data && mappingsResponse.data.mappings.length > 0) {
            setPitcheroMappings(mappingsResponse.data.mappings || []);
            setAvailableChildren(mappingsResponse.data.children || []);

            // Show mappings review screen directly
            setCurrentStep('pitchero_mappings_review');
          } else {
            // Mappings empty but sync didn't fail - this is unexpected
            alert('✅ Connected to Pitchero!\n\nNo children mappings were created. You can trigger a sync from your profile later.');

            // Check if there are other integrations to complete
            const hasOtherIntegrations = selectedClubApps.includes('socs') ||
                                          selectedClubApps.includes('schoolportal') ||
                                          selectedClubApps.includes('schoolsbuddy');
            if (hasOtherIntegrations) {
              // Mark pitchero as completed and continue with other integrations
              setPitcheroCompleted(true);
              setCurrentStep('integrations_setup');
            } else {
              // Check if we should offer term planner
              const [childrenResponse, parentsResponse] = await Promise.all([
                apiService.getChildren(),
                apiService.getParents()
              ]);
              if (childrenResponse.success && childrenResponse.data && childrenResponse.data.length > 0) {
                setTermPlannerChildren(childrenResponse.data.filter((c: any) => !c.is_parent));
                if (parentsResponse.success && parentsResponse.data) {
                  setTermPlannerParents(parentsResponse.data);
                }
                await navigateToTermPlannerSetup(childrenResponse.data);
              } else {
                completeOnboarding();
              }
            }
          }
        } else {
          alert(`Sync failed: ${syncResponse.error || 'Unknown error'}\n\nPlease check your credentials and try again.`);
          // Clear the input fields so user can re-enter
          setPitcheroUsername('');
          setPitcheroPassword('');
        }
      } else {
        alert(`Failed to connect: ${response.error || 'Unknown error'}`);
      }
    } catch (error) {
      console.error('Failed to connect Pitchero:', error);
      alert('Failed to connect to Pitchero. Please try again.');
    } finally {
      setConnectingIntegration(false);
    }
  };

  const handleSkipIntegrations = async () => {
    // Check if we're skipping integrations - remove uncompleted ones
    let currentApps = [...selectedClubApps];

    // Remove Pitchero if not connected
    const pitcheroIndex = currentApps.indexOf('pitchero');
    if (pitcheroIndex !== -1) {
      currentApps.splice(pitcheroIndex, 1);
    }

    // Remove SchoolsBuddy if not completed
    if (!schoolsBuddyCompleted) {
      const schoolsBuddyIndex = currentApps.indexOf('schoolsbuddy');
      if (schoolsBuddyIndex !== -1) {
        currentApps.splice(schoolsBuddyIndex, 1);
      }
    }

    setSelectedClubApps(currentApps);

    // If SOCS is still in the list, open the SOCS modal
    if (currentApps.includes('socs')) {
      try {
        const childrenResponse = await apiService.getChildren();
        if (childrenResponse.success && childrenResponse.data) {
          setChildrenForSOCS(childrenResponse.data);
          setShowSOCSModal(true);
        } else {
          alert('Failed to load children for SOCS setup');
        }
      } catch (error) {
        console.error('Failed to load children:', error);
        alert('Failed to load children for SOCS setup');
      }
      return;
    }

    // No more integrations to set up - load children and go to term planner
    try {
      const [childrenResponse, parentsResponse] = await Promise.all([
        apiService.getChildren(),
        apiService.getParents()
      ]);

      if (childrenResponse.success && childrenResponse.data && childrenResponse.data.length > 0) {
        setTermPlannerChildren(childrenResponse.data.filter((c: any) => !c.is_parent));
        if (parentsResponse.success && parentsResponse.data) {
          setTermPlannerParents(parentsResponse.data);
        }
        await navigateToTermPlannerSetup(childrenResponse.data);
      } else {
        completeOnboarding();
      }
    } catch (error) {
      console.error('Failed to load children:', error);
      completeOnboarding();
    }
  };

  const handleSetupSkeletonChild = (child: SkeletonChild) => {
    // Set up the selected child and populate form
    setSelectedSkeletonChild(child);
    setChildFirstName(child.first_name || '');
    setChildLastName(child.last_name || '');
    setChildBirthDate(child.birth_date || '');

    // Show the skeleton DOB modal
    setCurrentStep('skeleton_dob');
  };

  const handleUpdateChildDOB = async () => {
    if (!selectedSkeletonChild || !childBirthDate) return;

    setUpdatingChild(true);
    try {
      await apiService.updateChild(selectedSkeletonChild.uuid, {
        first_name: childFirstName.trim() || selectedSkeletonChild.first_name,
        last_name: childLastName.trim() || selectedSkeletonChild.last_name,
        birth_date: childBirthDate,
        is_skeleton: false
      });


      // Remove this child from the skeleton children list
      const updatedSkeletonChildren = skeletonChildren.filter(
        child => child.uuid !== selectedSkeletonChild.uuid
      );
      setSkeletonChildren(updatedSkeletonChildren);

      // Reset form
      setSelectedSkeletonChild(null);
      setChildBirthDate('');
      setChildFirstName('');
      setChildLastName('');

      // Check if there are more skeleton children to process
      if (updatedSkeletonChildren.length > 0) {
        setCurrentStep('child_setup');
      } else {
        // All skeleton children processed, stay on child setup so user can add more or skip
        setCurrentStep('child_setup');
      }
    } catch (error) {
      console.error('Failed to update skeleton child DOB:', error);
    } finally {
      setUpdatingChild(false);
    }
  };

  const renderStep1 = () => {
    const hasConnections = connectionsWaitingCount > 0;

    return (
      <div className="onboarding-step">
        <div className="step-header">
          <h2>👋 Welcome to Cluubz!</h2>
          <p>
            {hasConnections
              ? `Great news! You have ${connectionsWaitingCount} connection${connectionsWaitingCount !== 1 ? 's' : ''} waiting for you.`
              : "Let's get your family set up in just a few steps."
            }
          </p>
        </div>

        <div className="step-content">
          <div className="family-setup-card">
            {allParents.length > 1 ? (
              <>
                <h3>👫 Parents in Your Account</h3>
                <div style={{ marginBottom: '20px' }}>
                  {allParents.map((parent: any) => (
                    <div key={parent.uuid} style={{
                      display: 'flex',
                      alignItems: 'center',
                      padding: '12px',
                      backgroundColor: '#f0f9f0',
                      borderRadius: '8px',
                      marginBottom: '8px',
                      border: '1px solid #c3e6cb'
                    }}>
                      <span style={{ fontSize: '20px', marginRight: '12px' }}>👤</span>
                      <div>
                        <div style={{ fontWeight: 500 }}>
                          {parent.username || parent.email}
                          {parent.is_primary && <span style={{ fontSize: '12px', color: '#666', marginLeft: '8px' }}>(Primary)</span>}
                        </div>
                        {parent.role && parent.role !== 'parent' && (
                          <div style={{ fontSize: '13px', color: '#666', textTransform: 'capitalize' }}>
                            {parent.role}
                          </div>
                        )}
                      </div>
                    </div>
                  ))}
                </div>

                <div className="step-actions" style={{ display: 'flex', gap: '12px', flexDirection: 'column' }}>
                  <button
                    className="option-button"
                    onClick={handleAddParent}
                    style={{ width: '100%', padding: '14px 24px', fontSize: '15px', backgroundColor: '#f8f9fa', border: '1px solid #dee2e6' }}
                  >
                    + Add Another Parent
                  </button>
                  <button
                    className="option-button primary"
                    onClick={() => setCurrentStep('add_children_prompt')}
                    style={{ width: '100%', padding: '18px 24px', fontSize: '16px' }}
                  >
                    Continue
                  </button>
                </div>
              </>
            ) : (
              <>
                <h3>👫 Add Another Parent</h3>
                <p>
                  Would you like to add another parent to share access to your family account?
                </p>

                <div className="parent-benefits" style={{ margin: '20px 0', padding: '15px', backgroundColor: '#f8f9fa', borderRadius: '8px' }}>
                  <p style={{ fontWeight: 500, marginBottom: '10px' }}>Benefits of adding another parent:</p>
                  <ul style={{ margin: 0, paddingLeft: '20px' }}>
                    <li>Both parents can manage activities and connections</li>
                    <li>Share calendar and notifications</li>
                    <li>Joint family organization</li>
                  </ul>
                </div>

                <div className="step-actions" style={{ display: 'flex', gap: '12px', flexDirection: 'column' }}>
                  <button
                    className="option-button primary"
                    onClick={handleAddParent}
                    style={{ width: '100%', padding: '18px 24px', fontSize: '16px' }}
                  >
                    Add Another Parent
                  </button>
                  <button
                    className="option-button secondary"
                    onClick={() => setCurrentStep('add_children_prompt')}
                    style={{ width: '100%', padding: '18px 24px', fontSize: '16px' }}
                  >
                    Skip for Now
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      </div>
    );
  };

  const renderAddChildrenPrompt = () => {
    return (
      <div className="onboarding-step">
        <div className="step-header">
          <h2>👶 Set Up Your Children</h2>
          <p>Add your children to start organizing their activities.</p>
        </div>

        <div className="step-content">
          <div className="family-setup-card">
            {allChildren.filter((c: any) => !c.is_parent).length > 0 ? (
              <>
                <h3>🏠 Your Children</h3>
                <div style={{ marginBottom: '20px' }}>
                  {allChildren.filter((c: any) => !c.is_parent).map((child: any) => (
                    <div key={child.uuid} style={{
                      display: 'flex',
                      alignItems: 'center',
                      padding: '12px',
                      backgroundColor: '#f0f9f0',
                      borderRadius: '8px',
                      marginBottom: '8px',
                      border: '1px solid #c3e6cb'
                    }}>
                      <span style={{ fontSize: '20px', marginRight: '12px' }}>👶</span>
                      <div>
                        <div style={{ fontWeight: 500 }}>
                          {child.display_name || `${child.first_name} ${child.last_name}`}
                        </div>
                        {child.school_venue && (
                          <div style={{ fontSize: '13px', color: '#666' }}>
                            {child.school_venue}
                          </div>
                        )}
                      </div>
                    </div>
                  ))}
                </div>

                <div className="step-actions" style={{ display: 'flex', gap: '12px', flexDirection: 'column' }}>
                  <button
                    className="option-button"
                    onClick={() => setShowAddChildModal(true)}
                    style={{ width: '100%', padding: '14px 24px', fontSize: '15px', backgroundColor: '#f8f9fa', border: '1px solid #dee2e6' }}
                  >
                    + Add Another Child
                  </button>
                  <button
                    className="option-button primary"
                    onClick={() => setCurrentStep('child_app_questionnaire')}
                    style={{ width: '100%', padding: '18px 24px', fontSize: '16px' }}
                  >
                    Continue
                  </button>
                </div>
              </>
            ) : (
              <div style={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                textAlign: 'center',
                padding: '20px 0'
              }}>
                <div style={{ fontSize: '64px', marginBottom: '16px' }}>👶</div>
                <h3 style={{ margin: '0 0 12px 0' }}>Add Your First Child</h3>
                <p style={{ margin: '0 0 24px 0', color: '#666' }}>
                  You need to add at least one child to continue.
                </p>
                <button
                  className="option-button primary"
                  onClick={handleContinueSolo}
                  style={{
                    width: '100%',
                    maxWidth: '280px',
                    padding: '18px 24px',
                    fontSize: '16px'
                  }}
                >
                  + Add Child
                </button>
              </div>
            )}
          </div>
        </div>
      </div>
    );
  };

  const renderParentAddedStep = () => (
    <div className="onboarding-step">
      <div className="step-header">
        <h2>🎉 Welcome to the Family!</h2>
        <p>You've successfully joined {userInfo?.family_name || 'your family'} account.</p>
      </div>

      <div className="step-content">
        <div className="family-setup-card">
          <h3>👨‍👩‍👧‍👦 Second Parent Setup Complete</h3>
          <p>
            Great! You now have access to your family's account with {userInfo?.skeletonMerge?.mergedConnections || 0} existing connections.
            Let's continue setting up your children.
          </p>

          <div className="setup-options">
            <div className="option-card">
              <div className="option-header">
                <span className="option-icon">👶</span>
                <span className="option-title">Set Up Children</span>
              </div>
              <p>Complete your children's profiles and start organizing activities together.</p>
              <button
                className="option-button primary"
                onClick={handleParentAdded}
              >
                Continue to Child Setup
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );

  const renderChildSetupStep = () => (
    <div className="onboarding-step">
      <div className="step-header">
        <h2>👶 Complete Children Profiles</h2>
        <p>Let's complete your children's information to get started.</p>
      </div>

      <div className="step-content">
        <div className="family-setup-card">
          <h3>🔄 Skeleton Children Found</h3>
          <p>
            We found {skeletonChildren.length} child{skeletonChildren.length !== 1 ? 'ren' : ''} in your account that need some additional information.
          </p>

          <div className="skeleton-children-list">
            {skeletonChildren.map((child, index) => (
              <div key={child.uuid} className="skeleton-child-card">
                <div className="child-info">
                  <span className="child-name">
                    {child.first_name && child.last_name
                      ? `${child.first_name} ${child.last_name}`
                      : (child as any).name || 'Unnamed Child'
                    }
                  </span>
                </div>
                <button
                  className="option-button primary"
                  onClick={() => handleSetupSkeletonChild(child)}
                >
                  Complete Profile
                </button>
              </div>
            ))}
          </div>

        </div>
      </div>
    </div>
  );

  const renderSkeletonDOBStep = () => {
    if (!selectedSkeletonChild) return null;

    return (
      <div className="onboarding-step">
        <div className="step-header">
          <h2>👶 Complete Child Profile</h2>
          <p>Let's complete the profile for this child who was added to your account.</p>
        </div>

        <div className="step-content">
          <div className="family-setup-card">
            <h3>✏️ Edit Child Information</h3>
            <p>Please review and update the child's information as needed.</p>

            <div className="form-group">
              <label htmlFor="child-first-name">First Name</label>
              <input
                type="text"
                id="child-first-name"
                value={childFirstName}
                onChange={(e) => setChildFirstName(e.target.value)}
                className="form-input"
                placeholder="Child's first name"
              />
            </div>

            <div className="form-group">
              <label htmlFor="child-last-name">Last Name</label>
              <input
                type="text"
                id="child-last-name"
                value={childLastName}
                onChange={(e) => setChildLastName(e.target.value)}
                className="form-input"
                placeholder="Child's last name"
              />
            </div>

            <div className="form-group">
              <label htmlFor="child-birth-date">Date of Birth</label>
              <input
                type="date"
                id="child-birth-date"
                value={childBirthDate}
                onChange={(e) => setChildBirthDate(e.target.value)}
                max={new Date().toISOString().split('T')[0]}
                className="form-input"
              />
              <small className="form-help">
                This helps us suggest age-appropriate activities and connections.
              </small>
            </div>

            <div className="step-actions">
              <button
                className="option-button secondary"
                onClick={() => {
                  setSelectedSkeletonChild(null);
                  setChildBirthDate('');
                  setChildFirstName('');
                  setChildLastName('');
                  setCurrentStep('child_setup');
                }}
                disabled={updatingChild}
              >
                Back
              </button>
              <button
                className="option-button primary"
                onClick={handleUpdateChildDOB}
                disabled={!childBirthDate || updatingChild}
              >
                {updatingChild ? 'Saving...' : 'Save & Continue'}
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  };

  const loadExistingIntegrations = async () => {
    try {
      const integrations: {[key: string]: boolean} = {};

      // Check Pitchero status if we have a primary parent
      if (primaryParentUuid) {
        try {
          const pitcheroStatus = await apiService.getPitcheroStatus(primaryParentUuid);
          if (pitcheroStatus.success && pitcheroStatus.data?.hasCredentials) {
            integrations['pitchero'] = true;
          }
        } catch (error) {
          console.error('Failed to check Pitchero status:', error);
        }
      }

      // Check Spond status — only mark as configured if it has matched children
      if (primaryParentUuid) {
        try {
          const spondStatus = await apiService.getSpondStatus(primaryParentUuid);
          if (spondStatus.success && spondStatus.data?.connected && spondStatus.data?.matched_children > 0) {
            integrations['spond'] = true;
          }
        } catch (error) {
          console.error('Failed to check Spond status:', error);
        }
      }

      // Check SOCS mappings - if any child has SOCS mappings, mark as configured
      try {
        const childrenResponse = await apiService.getChildren();
        if (childrenResponse.success && childrenResponse.data) {
          for (const child of childrenResponse.data) {
            try {
              const socsMappings = await apiService.socsGetChildMappings(child.uuid);
              if (socsMappings.success && socsMappings.data && socsMappings.data.length > 0) {
                integrations['socs'] = true;
                break; // Found SOCS, no need to check more children
              }
            } catch (error) {
              console.error(`Failed to check SOCS mappings for child ${child.uuid}:`, error);
            }
          }
        }
      } catch (error) {
        console.error('Failed to check SOCS mappings:', error);
      }

      setExistingIntegrations(integrations);
    } catch (error) {
      console.error('Failed to load existing integrations:', error);
    }
  };

  // Get children who selected a specific app in the questionnaire
  const getChildrenForApp = (appId: string): any[] => {
    return allChildren.filter((child: any) => {
      if (child.is_parent) return false;
      const sel = childAppSelections[child.uuid];
      if (!sel) return false;
      return [...sel.sportsAndSocial, ...sel.schoolEvents,
              ...sel.sportingFixtures, ...sel.clubsAndExtraCurricular].includes(appId);
    });
  };

  const getChildName = (child: any) => {
    return child.first_name || child.name?.split(' ')[0] || 'your child';
  };

  const getQuestionnaireChildren = () => {
    return allChildren.filter((c: any) => !c.is_parent);
  };

  const handleQuestionnaireAppToggle = (childUuid: string, categoryKey: AppCategoryKey, appId: string) => {
    setChildAppSelections(prev => {
      const childSel = prev[childUuid] || {
        sportsAndSocial: [],
        schoolEvents: [],
        sportingFixtures: [],
        clubsAndExtraCurricular: []
      };
      const currentApps = childSel[categoryKey];
      const updatedApps = currentApps.includes(appId)
        ? currentApps.filter(id => id !== appId)
        : [...currentApps, appId];
      return {
        ...prev,
        [childUuid]: {
          ...childSel,
          [categoryKey]: updatedApps
        }
      };
    });
  };

  const handleQuestionnaireNext = async () => {
    const children = getQuestionnaireChildren();
    if (currentQuestionCategory < APP_CATEGORIES.length - 1) {
      // Next category for same child
      setCurrentQuestionCategory(prev => prev + 1);
    } else if (currentQuestionnaireChildIndex < children.length - 1) {
      // Next child - show intro screen first
      setCurrentQuestionnaireChildIndex(prev => prev + 1);
      setCurrentQuestionCategory(0);
      setShowChildIntroScreen(true);
    } else {
      // All done - aggregate selections and proceed
      await handleContinueFromQuestionnaire();
    }
  };

  const handleQuestionnaireBack = () => {
    if (showChildIntroScreen) {
      // On intro screen - go back to previous child's last question or previous step
      if (currentQuestionnaireChildIndex > 0) {
        setCurrentQuestionnaireChildIndex(prev => prev - 1);
        setCurrentQuestionCategory(APP_CATEGORIES.length - 1);
        setShowChildIntroScreen(false);
      } else {
        goToPreviousStep();
      }
    } else if (currentQuestionCategory > 0) {
      setCurrentQuestionCategory(prev => prev - 1);
    } else {
      // At first question for this child - go back to intro
      setShowChildIntroScreen(true);
    }
  };

  const handleContinueFromQuestionnaire = async () => {
    // Aggregate all children's selections into a deduplicated flat list
    const allApps = new Set<string>();
    Object.values(childAppSelections).forEach(sel => {
      [...sel.sportsAndSocial, ...sel.schoolEvents,
       ...sel.sportingFixtures, ...sel.clubsAndExtraCurricular]
        .forEach(app => allApps.add(app));
    });
    const aggregated = Array.from(allApps);
    console.log('🔍 handleContinueFromQuestionnaire - childAppSelections:', JSON.stringify(childAppSelections));
    console.log('🔍 handleContinueFromQuestionnaire - aggregated apps:', aggregated);
    setSelectedClubApps(aggregated);

    if (aggregated.length === 0) {
      // No apps selected - skip to term planner or complete
      try {
        const [childrenResponse, parentsResponse] = await Promise.all([
          apiService.getChildren(),
          apiService.getParents()
        ]);
        if (childrenResponse.success && childrenResponse.data && childrenResponse.data.length > 0) {
          setTermPlannerChildren(childrenResponse.data.filter((c: any) => !c.is_parent));
          if (parentsResponse.success && parentsResponse.data) {
            setTermPlannerParents(parentsResponse.data);
          }
          await navigateToTermPlannerSetup(childrenResponse.data);
        } else {
          completeOnboarding();
        }
      } catch (error) {
        completeOnboarding();
      }
    } else {
      setCurrentStep('integrations_setup');
    }
  };

  const renderChildAppQuestionnaire = () => {
    const children = getQuestionnaireChildren();

    if (children.length === 0) {
      // This step has its own nav bar, so without a way out this screen is a
      // dead end - offer the action it is actually asking for.
      return (
        <div className="onboarding-step">
          <div className="step-header">
            <h2>No children added yet</h2>
            <p>Let's add your children first.</p>
          </div>
          <button
            className="btn-primary"
            style={{ marginTop: '16px' }}
            onClick={() => setCurrentStep('add_children_prompt')}
          >
            Add a child
          </button>
        </div>
      );
    }

    const currentChild = children[currentQuestionnaireChildIndex] || children[0];
    const childName = getChildName(currentChild);

    // Show intro screen before each child's questions
    if (showChildIntroScreen) {
      return (
        <div className="onboarding-step" style={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
          <div style={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            flex: 1,
            textAlign: 'center',
            padding: '20px'
          }}>
            <div style={{ fontSize: '64px', marginBottom: '20px' }}>
              {currentQuestionnaireChildIndex === 0 ? '🎯' : '👋'}
            </div>
            <h2 style={{ fontSize: '1.4rem', marginBottom: '12px', color: '#333' }}>
              Let's collect all of {childName}'s activities
            </h2>
            <p style={{ fontSize: '1rem', color: '#666', maxWidth: '320px', lineHeight: 1.5, marginBottom: '32px' }}>
              We'll ask a few quick questions about the apps and platforms you use for {childName}.
            </p>
            {children.length > 1 && (
              <div style={{
                fontSize: '14px',
                color: '#999',
                marginBottom: '24px'
              }}>
                Child {currentQuestionnaireChildIndex + 1} of {children.length}
              </div>
            )}
            <button
              onClick={() => setShowChildIntroScreen(false)}
              style={{
                padding: '14px 40px',
                fontSize: '16px',
                fontWeight: 600,
                backgroundColor: '#1976d2',
                color: 'white',
                border: 'none',
                borderRadius: '8px',
                cursor: 'pointer'
              }}
            >
              Let's go
            </button>
          </div>
        </div>
      );
    }

    const category = APP_CATEGORIES[currentQuestionCategory];
    const childUuid = currentChild.uuid;
    const childSel = childAppSelections[childUuid] || {
      sportsAndSocial: [],
      schoolEvents: [],
      sportingFixtures: [],
      clubsAndExtraCurricular: []
    };
    const selectedAppsForCategory = childSel[category.key];

    const totalSteps = children.length * APP_CATEGORIES.length;
    const currentStepNum = currentQuestionnaireChildIndex * APP_CATEGORIES.length + currentQuestionCategory + 1;

    return (
      <div className="onboarding-step" style={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
        {/* Child indicator */}
        {children.length > 1 && (
          <div style={{
            textAlign: 'center',
            padding: '8px 16px',
            backgroundColor: '#e7f3ff',
            borderRadius: '8px',
            marginBottom: '8px',
            fontSize: '14px',
            color: '#004085',
            fontWeight: 500
          }}>
            Setting up {childName} ({currentQuestionnaireChildIndex + 1} of {children.length})
          </div>
        )}

        {/* Category progress dots */}
        <div style={{
          display: 'flex',
          justifyContent: 'center',
          gap: '8px',
          marginBottom: '16px'
        }}>
          {APP_CATEGORIES.map((_, idx) => (
            <div
              key={idx}
              style={{
                width: '10px',
                height: '10px',
                borderRadius: '50%',
                backgroundColor: idx === currentQuestionCategory ? '#1976d2' : idx < currentQuestionCategory ? '#4CAF50' : '#e0e0e0',
                transition: 'background-color 0.3s'
              }}
            />
          ))}
        </div>

        <div className="step-header" style={{ marginBottom: '8px' }}>
          <h2 style={{ fontSize: '1.2rem', lineHeight: 1.3 }}>{category.title(childName)}</h2>
          <p style={{ fontSize: '14px', color: '#666' }}>{category.subtitle}</p>
        </div>

        <div className="step-content" style={{
          display: 'flex',
          flexDirection: 'column',
          flex: 1,
          minHeight: 0
        }}>
          <div style={{
            flex: '1 1 auto',
            overflowY: 'auto',
            overflowX: 'hidden',
            paddingRight: '8px',
            marginBottom: '16px',
            minHeight: '120px'
          }}>
            <div className="club-apps-grid" style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
              gap: '12px',
              padding: '4px'
            }}>
              {category.apps.map(app => {
                const isExisting = existingIntegrations[app.id] === true;
                const isComingSoon = 'comingSoon' in app && app.comingSoon;
                const isDisabled = isComingSoon || isExisting;
                const isSelected = selectedAppsForCategory.includes(app.id);

                return (
                  <div
                    key={app.id}
                    className={`club-app-card ${isSelected ? 'selected' : ''} ${isDisabled ? 'disabled' : ''}`}
                    onClick={() => !isDisabled && handleQuestionnaireAppToggle(childUuid, category.key, app.id)}
                    style={{
                      border: isSelected ? '2px solid #4CAF50' : '2px solid #e0e0e0',
                      borderRadius: '12px',
                      padding: '16px',
                      cursor: isDisabled ? 'not-allowed' : 'pointer',
                      backgroundColor: isSelected ? '#f0f8f0' : 'white',
                      opacity: isDisabled ? 0.6 : 1,
                      transition: 'all 0.2s',
                      position: 'relative',
                      minHeight: '120px'
                    }}
                  >
                    {isComingSoon && (
                      <span style={{
                        position: 'absolute',
                        top: '8px',
                        right: '8px',
                        background: '#ff9800',
                        color: 'white',
                        padding: '4px 8px',
                        borderRadius: '4px',
                        fontSize: '11px',
                        fontWeight: 'bold'
                      }}>
                        COMING SOON
                      </span>
                    )}
                    {isExisting && (
                      <span style={{
                        position: 'absolute',
                        top: '8px',
                        right: '8px',
                        background: '#2196F3',
                        color: 'white',
                        padding: '4px 8px',
                        borderRadius: '4px',
                        fontSize: '11px',
                        fontWeight: 'bold'
                      }}>
                        ALREADY CONFIGURED
                      </span>
                    )}
                    <div style={{ fontSize: '32px', marginBottom: '8px' }}>{app.icon}</div>
                    <h3 style={{ marginBottom: '4px', fontSize: '16px' }}>{app.name}</h3>
                    <p style={{ fontSize: '13px', color: '#666', margin: 0 }}>{app.description}</p>
                    {isSelected && (
                      <div style={{
                        marginTop: '8px',
                        color: '#4CAF50',
                        fontWeight: 'bold',
                        fontSize: '14px'
                      }}>
                        ✓ Selected
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>

          {/* Questionnaire navigation */}
          <div style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            gap: '12px',
            paddingTop: '12px',
            borderTop: '1px solid #eee'
          }}>
            <button
              onClick={handleQuestionnaireBack}
              style={{
                padding: '10px 20px',
                backgroundColor: '#6c757d',
                color: 'white',
                border: 'none',
                borderRadius: '8px',
                cursor: 'pointer',
                fontSize: '14px',
                fontWeight: 500
              }}
            >
              Back
            </button>

            <button
              onClick={handleQuestionnaireNext}
              style={{
                padding: '10px 20px',
                backgroundColor: '#e0e0e0',
                color: '#333',
                border: 'none',
                borderRadius: '8px',
                cursor: 'pointer',
                fontSize: '14px'
              }}
            >
              None of these
            </button>

            <button
              onClick={handleQuestionnaireNext}
              disabled={selectedAppsForCategory.length === 0}
              style={{
                padding: '10px 20px',
                backgroundColor: selectedAppsForCategory.length > 0 ? '#1976d2' : '#ccc',
                color: 'white',
                border: 'none',
                borderRadius: '8px',
                cursor: selectedAppsForCategory.length > 0 ? 'pointer' : 'not-allowed',
                fontSize: '14px',
                fontWeight: 600
              }}
            >
              {currentQuestionnaireChildIndex === children.length - 1 && currentQuestionCategory === APP_CATEGORIES.length - 1
                ? 'Continue'
                : 'Next'}
            </button>
          </div>

          {/* Step counter */}
          <div style={{
            textAlign: 'center',
            fontSize: '12px',
            color: '#999',
            marginTop: '8px'
          }}>
            Question {currentStepNum} of {totalSteps}
          </div>
        </div>
      </div>
    );
  };

  // NOTE: SchoolsBuddy search and connect are now handled by SchoolsBuddySetupModal

  const renderIntegrationsSetup = () => {
    // Integrations are now handled via modals that auto-open via useEffect
    // This render function just shows a loading state while modals handle the work
    const hasPitchero = selectedClubApps.includes('pitchero');
    const hasSpond = selectedClubApps.includes('spond');
    const hasSocs = selectedClubApps.includes('socs');
    const hasSchoolPortal = selectedClubApps.includes('schoolportal');
    const hasSchoolsBuddy = selectedClubApps.includes('schoolsbuddy');

    // Show intro screen before launching integration modals
    if (showIntegrationsIntro) {
      const appNames: string[] = [];
      if (hasPitchero) appNames.push('Pitchero');
      if (hasSpond) appNames.push('Spond');
      if (hasSocs) appNames.push('SOCS');
      if (hasSchoolPortal) appNames.push('My School Portal');
      if (hasSchoolsBuddy) appNames.push('SchoolsBuddy');

      return (
        <div className="onboarding-step" style={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
          <div style={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            flex: 1,
            textAlign: 'center',
            padding: '20px'
          }}>
            <div style={{ fontSize: '64px', marginBottom: '20px' }}>🚀</div>
            <h2 style={{ fontSize: '1.4rem', marginBottom: '12px', color: '#333' }}>
              Right, let's get all the activities for you!
            </h2>
            <p style={{ fontSize: '1rem', color: '#666', maxWidth: '340px', lineHeight: 1.5, marginBottom: '24px' }}>
              We'll now connect to your apps and import all the activities automatically.
            </p>
            <div style={{
              display: 'flex',
              flexWrap: 'wrap',
              gap: '8px',
              justifyContent: 'center',
              marginBottom: '32px',
              maxWidth: '340px'
            }}>
              {appNames.map(name => (
                <span key={name} style={{
                  padding: '6px 14px',
                  backgroundColor: '#e7f3ff',
                  color: '#1976d2',
                  borderRadius: '20px',
                  fontSize: '14px',
                  fontWeight: 500
                }}>
                  {name}
                </span>
              ))}
            </div>
            <button
              onClick={() => setShowIntegrationsIntro(false)}
              style={{
                padding: '14px 40px',
                fontSize: '16px',
                fontWeight: 600,
                backgroundColor: '#1976d2',
                color: 'white',
                border: 'none',
                borderRadius: '8px',
                cursor: 'pointer'
              }}
            >
              Let's go
            </button>
          </div>
        </div>
      );
    }

    // Show SchoolsBuddy intro screen (per child)
    if (showSchoolsBuddyIntro && hasSchoolsBuddy && !schoolsBuddyCompleted) {
      // Build/use the children list for SchoolsBuddy
      let sbChildren = schoolsBuddyChildrenList;
      if (sbChildren.length === 0) {
        sbChildren = getChildrenForApp('schoolsbuddy');
        console.log('🔍 SB intro - getChildrenForApp returned:', sbChildren.map((c: any) => c.first_name || c.name));
        if (sbChildren.length === 0) {
          // No children were selected for SchoolsBuddy — skip
          setShowSchoolsBuddyIntro(false);
          setSchoolsBuddyCompleted(true);
          return null;
        }
        setSchoolsBuddyChildrenList(sbChildren);
      }
      const currentChild = sbChildren[schoolsBuddyChildIndex] || sbChildren[0];
      const childName = currentChild?.first_name || currentChild?.name?.split(' ')[0] || 'your child';
      const remainingChildren = sbChildren.slice(schoolsBuddyChildIndex + 1);

      return (
        <div className="onboarding-step" style={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
          <div style={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            flex: 1,
            textAlign: 'center',
            padding: '20px'
          }}>
            <div style={{ fontSize: '64px', marginBottom: '20px' }}>🎯</div>
            <h2 style={{ fontSize: '1.4rem', marginBottom: '12px', color: '#333' }}>
              Let's set up {childName}'s activities for the term
            </h2>
            <p style={{ fontSize: '1rem', color: '#666', maxWidth: '340px', lineHeight: 1.5, marginBottom: '16px' }}>
              We'll import any activities from your connected apps and set up {childName}'s term planner. You can add anything else manually.
            </p>
            {sbChildren.length > 1 && (
              <p style={{ fontSize: '0.9rem', color: '#999', maxWidth: '340px', lineHeight: 1.5, marginBottom: '16px' }}>
                {schoolsBuddyChildIndex === 0
                  ? `We'll then do the same for ${remainingChildren.map((c: any) => c.first_name || c.name?.split(' ')[0]).join(' and ')}.`
                  : `Child ${schoolsBuddyChildIndex + 1} of ${sbChildren.length}`
                }
              </p>
            )}
            <button
              onClick={() => {
                setShowSchoolsBuddyIntro(false);
                setShowSchoolsBuddyModal(true);
              }}
              style={{
                padding: '14px 40px',
                fontSize: '16px',
                fontWeight: 600,
                backgroundColor: '#1976d2',
                color: 'white',
                border: 'none',
                borderRadius: '8px',
                cursor: 'pointer',
                marginTop: '16px'
              }}
            >
              Let's go
            </button>
          </div>
        </div>
      );
    }

    // Determine what's currently being set up
    let currentIntegration = '';
    let integrationIcon = '';
    if (hasPitchero && !pitcheroCompleted) {
      currentIntegration = 'Pitchero';
      integrationIcon = '🏟️';
    } else if (hasSpond && !spondCompleted) {
      currentIntegration = 'Spond';
      integrationIcon = '📱';
    } else if (hasSocs && !socsCompleted) {
      currentIntegration = 'SOCS';
      integrationIcon = '🏫';
    } else if (hasSchoolPortal && !schoolPortalCompleted) {
      currentIntegration = 'My School Portal';
      integrationIcon = '🗓️';
    } else if (hasSchoolsBuddy && !schoolsBuddyCompleted) {
      currentIntegration = 'SchoolsBuddy';
      integrationIcon = '📚';
    }

    // Show progress of completed integrations
    const completedIntegrations = [];
    if (hasPitchero && pitcheroCompleted) completedIntegrations.push('Pitchero');
    if (hasSpond && spondCompleted) completedIntegrations.push('Spond');
    if (hasSocs && socsCompleted) completedIntegrations.push('SOCS');
    if (hasSchoolPortal && schoolPortalCompleted) completedIntegrations.push('My School Portal');
    if (hasSchoolsBuddy && schoolsBuddyCompleted) completedIntegrations.push('SchoolsBuddy');

    // Determine next integration for completion screens
    let nextIntegration = '';
    let nextIntegrationIcon = '';
    if (showPitcheroCompletionScreen) {
      if (hasSpond && !spondCompleted) {
        nextIntegration = 'Spond';
        nextIntegrationIcon = '📱';
      } else if (hasSocs && !socsCompleted) {
        nextIntegration = 'SOCS';
        nextIntegrationIcon = '🏫';
      } else if (hasSchoolPortal && !schoolPortalCompleted) {
        nextIntegration = 'My School Portal';
        nextIntegrationIcon = '🗓️';
      } else if (hasSchoolsBuddy && !schoolsBuddyCompleted) {
        nextIntegration = 'SchoolsBuddy';
        nextIntegrationIcon = '📚';
      }
    } else if (showSpondCompletionScreen) {
      if (hasSocs && !socsCompleted) {
        nextIntegration = 'SOCS';
        nextIntegrationIcon = '🏫';
      } else if (hasSchoolPortal && !schoolPortalCompleted) {
        nextIntegration = 'My School Portal';
        nextIntegrationIcon = '🗓️';
      } else if (hasSchoolsBuddy && !schoolsBuddyCompleted) {
        nextIntegration = 'SchoolsBuddy';
        nextIntegrationIcon = '📚';
      }
    } else if (showSOCSCompletionScreen) {
      if (hasSchoolPortal && !schoolPortalCompleted) {
        nextIntegration = 'My School Portal';
        nextIntegrationIcon = '🗓️';
      } else if (hasSchoolsBuddy && !schoolsBuddyCompleted) {
        nextIntegration = 'SchoolsBuddy';
        nextIntegrationIcon = '📚';
      }
    } else if (showSchoolPortalCompletionScreen) {
      if (hasSchoolsBuddy && !schoolsBuddyCompleted) {
        nextIntegration = 'SchoolsBuddy';
        nextIntegrationIcon = '📚';
      }
    }

    // Render completion screen if showing
    if (showPitcheroCompletionScreen || showSpondCompletionScreen || showSOCSCompletionScreen || showSchoolPortalCompletionScreen || showSchoolsBuddyCompletionScreen) {
      let completedName = '';
      let completedIcon = '';
      if (showPitcheroCompletionScreen) {
        completedName = 'Pitchero';
        completedIcon = '🏟️';
      } else if (showSpondCompletionScreen) {
        completedName = 'Spond';
        completedIcon = '📱';
      } else if (showSOCSCompletionScreen) {
        completedName = 'SOCS';
        completedIcon = '🏫';
      } else if (showSchoolPortalCompletionScreen) {
        completedName = 'My School Portal';
        completedIcon = '🗓️';
      } else if (showSchoolsBuddyCompletionScreen) {
        completedName = 'SchoolsBuddy';
        completedIcon = '📚';
      }

      const isLastIntegration = showSchoolsBuddyCompletionScreen ||
        (showSchoolPortalCompletionScreen && !hasSchoolsBuddy) ||
        (showSOCSCompletionScreen && !hasSchoolPortal && !hasSchoolsBuddy) ||
        (showSpondCompletionScreen && !hasSocs && !hasSchoolPortal && !hasSchoolsBuddy) ||
        (showPitcheroCompletionScreen && !hasSpond && !hasSocs && !hasSchoolPortal && !hasSchoolsBuddy);

      // SchoolsBuddy falls through to the generic per-integration "Connected!"
      // screen below — no SB-specific copy. The "Let's set up X's activities"
      // intro is owned by ProfileScreen's nextChildIntroSlide and fires before
      // each TP editor opens.

      return (
        <div className="onboarding-step">
          <div style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            minHeight: '300px',
            padding: '20px'
          }}>
            <div style={{ textAlign: 'center', maxWidth: '320px', width: '100%' }}>
              <div style={{ fontSize: '56px', marginBottom: '16px' }}>{completedIcon}</div>
              <h2 style={{ color: '#28a745', marginBottom: '12px', fontSize: '1.3rem' }}>
                {completedName} Connected!
              </h2>
              <p style={{ color: '#666', fontSize: '0.95rem', marginBottom: '24px' }}>
                {isLastIntegration
                  ? 'Your activities and clubs have been imported. Now let\'s set up the rest of your term planner.'
                  : 'Your activities and clubs have been imported.'}
              </p>
              <button
                onClick={handleContinueFromCompletionScreen}
                style={{
                  padding: '14px 32px',
                  fontSize: '16px',
                  fontWeight: 600,
                  backgroundColor: '#1976d2',
                  color: 'white',
                  border: 'none',
                  borderRadius: '8px',
                  cursor: 'pointer'
                }}
              >
                {nextIntegration ? `Continue to ${nextIntegration}` : 'Set up term planner'}
              </button>
            </div>
          </div>
        </div>
      );
    }

    return (
      <div className="onboarding-step">
        <div className="step-header">
          <h2>🔐 Connect Your Integrations</h2>
          <p>Setting up your selected club apps...</p>
        </div>

        <div className="step-content">
          <div className="family-setup-card" style={{ textAlign: 'center', padding: '40px 20px' }}>
            {currentIntegration ? (
              <>
                <div style={{ fontSize: '48px', marginBottom: '16px' }}>{integrationIcon}</div>
                <h3>Setting up {currentIntegration}</h3>
                <p style={{ color: '#666', marginBottom: '20px' }}>
                  A modal will open to connect your {currentIntegration} account.
                </p>
                <div style={{
                  width: '40px',
                  height: '40px',
                  border: '4px solid #e0e0e0',
                  borderTop: '4px solid #1976d2',
                  borderRadius: '50%',
                  animation: 'spin 1s linear infinite',
                  margin: '0 auto'
                }} />
                <style>{`
                  @keyframes spin {
                    0% { transform: rotate(0deg); }
                    100% { transform: rotate(360deg); }
                  }
                `}</style>
              </>
            ) : (
              <>
                <div style={{
                  width: '48px',
                  height: '48px',
                  border: '4px solid #e0e0e0',
                  borderTop: '4px solid #1976d2',
                  borderRadius: '50%',
                  animation: 'spin 1s linear infinite',
                  margin: '0 auto 16px'
                }} />
                <h3>{transitioningToTermPlanner ? 'Generating Term Planner...' : 'Setting up...'}</h3>
                <p style={{ color: '#666' }}>
                  {transitioningToTermPlanner ? 'Setting up your schedule' : 'Moving to the next step...'}
                </p>
              </>
            )}

            {completedIntegrations.length > 0 && (
              <div style={{ marginTop: '24px', padding: '12px', backgroundColor: '#d4edda', borderRadius: '8px' }}>
                <p style={{ margin: 0, fontSize: '14px', color: '#155724' }}>
                  ✓ Connected: {completedIntegrations.join(', ')}
                </p>
              </div>
            )}
          </div>
        </div>
      </div>
    );
  };

  const handleMappingChange = async (mappingId: number, newChildUuid: string) => {
    if (!primaryParentUuid) return;

    try {
      const response = await apiService.updatePitcheroMapping(primaryParentUuid, mappingId, newChildUuid);

      if (response.success) {
        // Update local state
        setPitcheroMappings(prevMappings =>
          prevMappings.map(m =>
            m.id === mappingId
              ? {
                  ...m,
                  mappedChildUuid: newChildUuid,
                  mappedChildName: availableChildren.find(c => c.uuid === newChildUuid)?.name || ''
                }
              : m
          )
        );
      } else {
        alert('Failed to update mapping');
      }
    } catch (error) {
      console.error('Failed to update mapping:', error);
      alert('Failed to update mapping');
    }
  };

  const handleContinueFromMappings = async () => {
    if (!primaryParentUuid) return;

    try {
      setSavingMappings(true);

      // Import activities using the approved mappings
      alert('📥 Importing activities...\n\nThis may take a moment as we import your fixtures and training sessions.');

      const importResponse = await apiService.importPitcheroActivities(primaryParentUuid);

      if (importResponse.success) {
        const activitiesCount = (importResponse.data?.activitiesCreated || 0) + (importResponse.data?.activitiesUpdated || 0);

        alert(`✅ Import Complete!\n\n${activitiesCount} activities have been added to your calendar.\n\nYou'll receive notifications for upcoming fixtures and training sessions.`);

        // Check if there are other integrations to complete
        const hasOtherIntegrations = selectedClubApps.includes('socs') ||
                                      selectedClubApps.includes('schoolportal') ||
                                      selectedClubApps.includes('schoolsbuddy');

        if (hasOtherIntegrations) {
          // Mark pitchero as completed and continue with other integrations
          setPitcheroCompleted(true);
          setCurrentStep('integrations_setup');
        } else {
          // Check if we should offer term planner
          const [childrenResponse, parentsResponse] = await Promise.all([
            apiService.getChildren(),
            apiService.getParents()
          ]);
          if (childrenResponse.success && childrenResponse.data && childrenResponse.data.length > 0) {
            setTermPlannerChildren(childrenResponse.data.filter((c: any) => !c.is_parent));
            if (parentsResponse.success && parentsResponse.data) {
              setTermPlannerParents(parentsResponse.data);
            }
            await navigateToTermPlannerSetup(childrenResponse.data);
          } else {
            completeOnboarding();
          }
        }
      } else {
        alert(`Import failed: ${importResponse.error || 'Unknown error'}`);
      }
    } catch (error) {
      console.error('Failed to import activities:', error);
      alert('Failed to import activities. Please try again later from your profile.');
    } finally {
      setSavingMappings(false);
    }
  };

  // Save/restore form data when switching children
  useEffect(() => {
    if (selectedTermChild) {
      // Save current child's data if there was a previous selection
      const previousChild = Object.keys(childFormData).find(key =>
        childFormData[key].termName !== undefined
      );

      // Load saved data for newly selected child, or use defaults
      const savedData = childFormData[selectedTermChild];
      if (savedData) {
        setTermName(savedData.termName || '');
        setTermStartDate(savedData.termStartDate || '');
        setTermEndDate(savedData.termEndDate || '');
        setDailySchedules(savedData.dailySchedules || {
          0: { drop_off: {}, club_reminders: [], pick_up: {} },
          1: { drop_off: {}, club_reminders: [], pick_up: {} },
          2: { drop_off: {}, club_reminders: [], pick_up: {} },
          3: { drop_off: {}, club_reminders: [], pick_up: {} },
          4: { drop_off: {}, club_reminders: [], pick_up: {} },
          5: { drop_off: {}, club_reminders: [], pick_up: {} },
          6: { drop_off: {}, club_reminders: [], pick_up: {} }
        });
      } else {
        // Planners are open-ended: they start today and run until the parent
        // replaces them. A first-time user has no idea when term ends, which is
        // exactly why we stopped asking.
        const now = new Date();
        const month = now.getMonth(); // 0-indexed: Aug=7
        const startYear = month >= 7 ? now.getFullYear() : now.getFullYear() - 1;
        const endYear = startYear + 1;

        setTermName(`${startYear}/${endYear}`);
        setTermStartDate(`${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-${String(now.getDate()).padStart(2, '0')}`);
        setTermEndDate('');
        setDailySchedules({
          0: { drop_off: {}, club_reminders: [], pick_up: {} },
          1: { drop_off: {}, club_reminders: [], pick_up: {} },
          2: { drop_off: {}, club_reminders: [], pick_up: {} },
          3: { drop_off: {}, club_reminders: [], pick_up: {} },
          4: { drop_off: {}, club_reminders: [], pick_up: {} },
          5: { drop_off: {}, club_reminders: [], pick_up: {} },
          6: { drop_off: {}, club_reminders: [], pick_up: {} }
        });
      }
    }
  }, [selectedTermChild]);

  // Save current form data whenever it changes
  useEffect(() => {
    if (selectedTermChild) {
      setChildFormData((prev: any) => ({
        ...prev,
        [selectedTermChild]: {
          termName,
          termStartDate,
          termEndDate,
          dailySchedules
        }
      }));
    }
  }, [selectedTermChild, termName, termStartDate, termEndDate, dailySchedules]);

  const updateDaySchedule = (dayIndex: number, field: string, value: any) => {
    setDailySchedules((prev: any) => ({
      ...prev,
      [dayIndex]: {
        ...prev[dayIndex],
        [field]: value
      }
    }));
  };

  const addClubReminder = (dayIndex: number) => {
    setDailySchedules((prev: any) => ({
      ...prev,
      [dayIndex]: {
        ...prev[dayIndex],
        club_reminders: [
          ...(prev[dayIndex].club_reminders || []),
          { activity_name: '', kit_needed: '' }
        ]
      }
    }));
  };

  const removeClubReminder = (dayIndex: number, reminderIndex: number) => {
    setDailySchedules((prev: any) => ({
      ...prev,
      [dayIndex]: {
        ...prev[dayIndex],
        club_reminders: prev[dayIndex].club_reminders.filter((_: any, i: number) => i !== reminderIndex)
      }
    }));
  };

  const updateClubReminder = (dayIndex: number, reminderIndex: number, field: string, value: any) => {
    setDailySchedules((prev: any) => ({
      ...prev,
      [dayIndex]: {
        ...prev[dayIndex],
        club_reminders: prev[dayIndex].club_reminders.map((reminder: any, i: number) => {
          if (i === reminderIndex) {
            // If clearing club_name, also clear venue and city
            if (field === 'club_name' && value === '') {
              return { ...reminder, club_name: '', venue: '', city: '', kit_items: [] };
            }
            return { ...reminder, [field]: value };
          }
          return reminder;
        })
      }
    }));
  };

  const handleOpenClubModal = (type: 'drop_off' | 'pick_up') => {
    setClubModalType(type);
    setShowClubModal(true);
  };

  const updateClubKitItems = async (clubUuid: string, kitItems: string[]) => {
    if (!clubUuid || kitItems.length === 0) return;

    try {
      await apiService.updateClubKitItems(clubUuid, kitItems);
      console.log('✅ Updated club kit items');
    } catch (error) {
      console.error('Failed to update club kit items:', error);
    }
  };

  const handleClubSelect = (club: any) => {
    if (clubModalType === 'club_reminder') {
      // Update the specific club reminder
      const updatedReminders = [...(dailySchedules[currentDayTab].club_reminders || [])];

      if (currentClubReminderIndex === -1) {
        // Add new club reminder - include club's kit_items and UUID
        updatedReminders.push({
          club_name: club.name,
          club_uuid: club.uuid || null,
          activity_name: club.name,
          venue: club.venue || '',
          city: club.location || '',
          kit_needed: club.kit_items && club.kit_items.length > 0 ? 'Yes' : '',
          kit_items: club.kit_items || []
        });
      } else {
        // Update existing club reminder - update club's kit_items and UUID
        updatedReminders[currentClubReminderIndex] = {
          ...updatedReminders[currentClubReminderIndex],
          club_name: club.name,
          club_uuid: club.uuid || null,
          activity_name: club.name,
          venue: club.venue || '',
          city: club.location || '',
          kit_items: club.kit_items || []
        };
      }

      setDailySchedules((prev: any) => ({
        ...prev,
        [currentDayTab]: {
          ...prev[currentDayTab],
          club_reminders: updatedReminders
        }
      }));
    } else {
      const fieldType = clubModalType === 'drop_off' ? 'drop_off' : 'pick_up';
      updateDaySchedule(currentDayTab, fieldType, {
        ...dailySchedules[currentDayTab][fieldType],
        club_name: club.name,
        club_uuid: club.uuid || null,
        venue: club.venue || '',
        city: club.location || '',
        kit_items: club.kit_items || []
      });
    }
    setShowClubModal(false);
  };

  // SOCS Modal Handlers
  const handleOpenSOCSModal = async () => {
    try {
      const childrenResponse = await apiService.getChildren();
      if (childrenResponse.success && childrenResponse.data) {
        // Filter to only children who selected SOCS in the questionnaire
        const socsChildren = getChildrenForApp('socs');
        setChildrenForSOCS(socsChildren.length > 0 ? socsChildren : childrenResponse.data);
        setShowSOCSModal(true);
      } else {
        alert('Failed to load children for SOCS setup');
      }
    } catch (error) {
      console.error('Failed to load children:', error);
      alert('Failed to load children for SOCS setup');
    }
  };

  // Helper function to move to term planner after integrations complete
  const navigateToTermPlannerSetup = async (childrenData: any[]) => {
    const nonParentChildren = childrenData.filter((c: any) => !c.is_parent);
    if (nonParentChildren.length === 0) {
      completeOnboarding();
      return;
    }

    let termPlannersList: TermPlannerInfo[] = [];
    let childrenNeedingPlanners = nonParentChildren;

    if (primaryParentUuid) {
      try {
        const termPlannersResponse = await apiService.getTermPlanners(primaryParentUuid);
        if (termPlannersResponse.success && termPlannersResponse.data && termPlannersResponse.data.length > 0) {
          const existingTermPlanners = termPlannersResponse.data;
          const childUuidsWithPlanners = new Set(existingTermPlanners.map((tp: any) => tp.child_uuid));

          termPlannersList = existingTermPlanners.map((tp: any) => {
            const child = nonParentChildren.find((c: any) => c.uuid === tp.child_uuid);
            return {
              childUuid: tp.child_uuid,
              childName: child?.name || `${child?.first_name || ''} ${child?.last_name || ''}`.trim() || 'Unknown',
              termPlannerUuid: tp.uuid
            };
          }).sort((a: TermPlannerInfo, b: TermPlannerInfo) => a.childName.localeCompare(b.childName));

          childrenNeedingPlanners = nonParentChildren
            .filter((child: any) => !childUuidsWithPlanners.has(child.uuid))
            .sort((a: any, b: any) => {
              const nameA = a.name || `${a.first_name || ''} ${a.last_name || ''}`.trim();
              const nameB = b.name || `${b.first_name || ''} ${b.last_name || ''}`.trim();
              return nameA.localeCompare(nameB);
            });
        }
      } catch (error) {
        console.error('Failed to check existing term planners:', error);
      }
    }

    if (onNavigateToTermPlanner) {
      onNavigateToTermPlanner(termPlannersList, childrenNeedingPlanners);
    } else {
      completeOnboarding();
    }
  };

  const handleMoveToTermPlanner = async () => {
    try {
      const [childrenResponse] = await Promise.all([
        apiService.getChildren(),
        apiService.getParents()
      ]);

      if (!childrenResponse.success || !childrenResponse.data || childrenResponse.data.length === 0) {
        completeOnboarding();
        return;
      }

      await navigateToTermPlannerSetup(childrenResponse.data);
    } catch (error) {
      console.error('Failed to load children:', error);
      completeOnboarding();
    }
  };

  // Pitchero Modal Handler
  const handlePitcheroModalClose = (completed: boolean, mappings?: any[]) => {
    setShowPitcheroModal(false);

    if (!completed) {
      // User cancelled - go back to club apps selection
      setCurrentStep('child_app_questionnaire');
      return;
    }

    // Mark Pitchero as completed and always show the completion screen — user
    // needs to see confirmation that activities were imported before either the
    // next integration or the term-planner setup.
    setPitcheroCompleted(true);
    setShowPitcheroCompletionScreen(true);
  };

  // SchoolsBuddy Modal Handler
  const handleSchoolsBuddyModalClose = (completed: boolean) => {
    setShowSchoolsBuddyModal(false);

    if (!completed) {
      // User cancelled - check what to go back to
      if (selectedClubApps.includes('socs') && !socsCompleted) {
        // Go back to SOCS
        handleOpenSOCSModal();
      } else if (selectedClubApps.includes('pitchero') && !pitcheroCompleted) {
        // Go back to Pitchero
        setShowPitcheroModal(true);
      } else {
        setCurrentStep('child_app_questionnaire');
      }
      return;
    }

    // Check if there are more children to process
    if (schoolsBuddyChildIndex < schoolsBuddyChildrenList.length - 1) {
      // More children - open next child's SB modal directly. No pre-modal intro;
      // the "Let's set up X's activities" intro is now owned by ProfileScreen and
      // fires before the TP editor opens.
      setSchoolsBuddyChildIndex(prev => prev + 1);
      setShowSchoolsBuddyModal(true);
      return;
    }

    // All children done - mark SchoolsBuddy as completed and show the completion screen
    setSchoolsBuddyCompleted(true);
    setShowSchoolsBuddyCompletionScreen(true);
  };

  const handleSOCSModalClose = async (completed: boolean) => {
    setShowSOCSModal(false);

    // If user clicked Back/Skip (not completed), go back
    if (!completed) {
      // Check if Pitchero was also selected and completed - if so, stay on integrations
      // but the flow will check if SchoolsBuddy needs setup
      if (selectedClubApps.includes('pitchero') && !pitcheroCompleted) {
        setShowPitcheroModal(true);
      } else {
        setCurrentStep('child_app_questionnaire');
      }
      return;
    }

    // Mark SOCS as completed and always show the completion screen — user needs
    // to see confirmation that fixtures were imported before either the next
    // integration or the term-planner setup.
    setSocsCompleted(true);
    setShowSOCSCompletionScreen(true);
  };

  // School Portal Modal Handler
  const handleSchoolPortalModalClose = (completed: boolean) => {
    setShowSchoolPortalModal(false);

    if (!completed) {
      // User cancelled - check what to go back to
      if (selectedClubApps.includes('socs') && !socsCompleted) {
        handleOpenSOCSModal();
      } else if (selectedClubApps.includes('pitchero') && !pitcheroCompleted) {
        setShowPitcheroModal(true);
      } else {
        setCurrentStep('child_app_questionnaire');
      }
      return;
    }

    // Mark School Portal as completed and always show the completion screen —
    // user needs to see confirmation that events and clubs were imported before
    // either the next integration or the term-planner setup.
    setSchoolPortalCompleted(true);
    setShowSchoolPortalCompletionScreen(true);
  };

  // Handler for Continue button on completion screens
  const handleContinueFromCompletionScreen = async () => {
    const hasPitchero = selectedClubApps.includes('pitchero');
    const hasSpond = selectedClubApps.includes('spond');
    const hasSocs = selectedClubApps.includes('socs');
    const hasSchoolPortal = selectedClubApps.includes('schoolportal');
    const hasSchoolsBuddy = selectedClubApps.includes('schoolsbuddy');

    console.log('🔍 handleContinueFromCompletionScreen - selectedClubApps:', selectedClubApps);
    console.log('🔍 handleContinueFromCompletionScreen - has:', { hasPitchero, hasSpond, hasSocs, hasSchoolPortal, hasSchoolsBuddy });
    console.log('🔍 handleContinueFromCompletionScreen - completed:', { pitcheroCompleted, spondCompleted, socsCompleted, schoolPortalCompleted, schoolsBuddyCompleted });

    // Check if all integrations are now complete
    const allComplete = (!hasPitchero || pitcheroCompleted) &&
                        (!hasSpond || spondCompleted) &&
                        (!hasSocs || socsCompleted) &&
                        (!hasSchoolPortal || schoolPortalCompleted) &&
                        (!hasSchoolsBuddy || schoolsBuddyCompleted);

    if (allComplete) {
      // Last integration done - go directly to term planner without clearing completion screen first
      // This avoids the "All Integrations Complete!" flash
      setTransitioningToTermPlanner(true);
      await handleMoveToTermPlanner();
      return;
    }

    // More integrations to go - clear completion screens and let useEffect open the next one
    setShowPitcheroCompletionScreen(false);
    setShowSpondCompletionScreen(false);
    setShowSOCSCompletionScreen(false);
    setShowSchoolPortalCompletionScreen(false);
    setShowSchoolsBuddyCompletionScreen(false);
  };

  const renderPitcheroMappingsReview = () => {
    return (
      <div className="onboarding-step">
        <div className="step-header">
          <h2>🎯 Review Child Mappings</h2>
          <p>We've automatically matched your Pitchero children with your Cluubz children. Please verify these are correct:</p>
        </div>

        <div className="step-content">
          <div className="family-setup-card">
            <h3>📋 Pitchero Child Mappings</h3>

            {pitcheroMappings.length === 0 ? (
              <p>No children found in your Pitchero account. You can add them later.</p>
            ) : (
              <div style={{ marginTop: '16px' }}>
                {pitcheroMappings.map((mapping) => (
                  <div
                    key={mapping.id}
                    style={{
                      border: '1px solid #e0e0e0',
                      borderRadius: '8px',
                      padding: '16px',
                      marginBottom: '12px',
                      backgroundColor: 'white'
                    }}
                  >
                    <div style={{ marginBottom: '8px' }}>
                      <strong style={{ fontSize: '16px' }}>Pitchero: {mapping.pitcheroChildName}</strong>
                      {mapping.confidenceScore && (
                        <span
                          style={{
                            marginLeft: '8px',
                            padding: '2px 8px',
                            borderRadius: '4px',
                            fontSize: '12px',
                            backgroundColor:
                              mapping.confidenceScore >= 0.9
                                ? '#4CAF50'
                                : mapping.confidenceScore >= 0.7
                                ? '#FF9800'
                                : '#F44336',
                            color: 'white'
                          }}
                        >
                          {Math.round(mapping.confidenceScore * 100)}% match
                        </span>
                      )}
                    </div>

                    {mapping.clubs && mapping.clubs.length > 0 && (
                      <div style={{
                        marginBottom: '12px',
                        padding: '8px 12px',
                        backgroundColor: '#f0f8ff',
                        borderRadius: '4px',
                        fontSize: '13px',
                        color: '#555'
                      }}>
                        <strong>🏟️ Clubs:</strong> {mapping.clubs.join(', ')}
                      </div>
                    )}

                    <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                      <span style={{ color: '#666' }}>Maps to:</span>
                      <select
                        value={mapping.mappedChildUuid}
                        onChange={(e) => handleMappingChange(mapping.id, e.target.value)}
                        style={{
                          flex: 1,
                          padding: '8px 12px',
                          borderRadius: '4px',
                          border: '1px solid #ccc',
                          fontSize: '14px'
                        }}
                      >
                        {availableChildren.map((child) => (
                          <option key={child.uuid} value={child.uuid}>
                            {child.name}
                          </option>
                        ))}
                      </select>
                    </div>
                  </div>
                ))}
              </div>
            )}

            <div style={{ marginTop: '24px', padding: '12px', backgroundColor: '#f0f8ff', borderRadius: '8px' }}>
              <p style={{ margin: 0, fontSize: '14px', color: '#666' }}>
                💡 <strong>Tip:</strong> If a mapping looks incorrect, use the dropdown to select the correct child.
                These mappings determine which child receives notifications for each Pitchero activity.
              </p>
            </div>

          </div>
        </div>
      </div>
    );
  };

  const renderConnections = () => {
    const handleGoToConnections = () => {
      // Save current step so we can return
      localStorage.setItem('onboarding_current_step', 'connections');

      // Navigate to connections screen
      if (onNavigateToConnections) {
        onNavigateToConnections();
      }
    };

    return (
      <div className="onboarding-step">
        <div className="step-header">
          <h2>👥 Connect with Families</h2>
          <p>Share activities and coordinate with other parents</p>
        </div>

        <div className="step-content" style={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          textAlign: 'center',
          padding: '20px'
        }}>
          <div style={{ fontSize: '64px', marginBottom: '16px' }}>🤝</div>
          <p style={{ color: '#666', marginBottom: '24px', maxWidth: '280px' }}>
            Find other parents to share activities, coordinate carpools, and build your community.
          </p>
          <button
            onClick={handleGoToConnections}
            style={{
              width: '100%',
              maxWidth: '280px',
              padding: '16px 24px',
              backgroundColor: '#007bff',
              color: 'white',
              border: 'none',
              borderRadius: '8px',
              cursor: 'pointer',
              fontSize: '16px',
              fontWeight: '500',
              marginBottom: '12px'
            }}
          >
            Find Connections
          </button>
          <p style={{
            fontSize: '13px',
            color: '#999',
            fontStyle: 'italic'
          }}>
            You can skip this and add connections later
          </p>
        </div>
      </div>
    );
  };

  const renderAddToHomeScreen = () => {
    const handleInstallApp = async () => {
      if (deferredPrompt) {
        // Android/Chrome - use native install prompt
        deferredPrompt.prompt();
        const { outcome } = await deferredPrompt.userChoice;

        if (outcome === 'accepted') {
          console.log('User accepted the install prompt');
        }
        setDeferredPrompt(null);
      }
      // Complete onboarding after attempting install
      completeOnboarding();
    };

    const handleSkipInstall = () => {
      completeOnboarding();
    };

    return (
      <div className="onboarding-step">
        <div className="step-header">
          <h2>Add Cluubz to Your Home Screen</h2>
          <p>Quick access to your family activities, schedules, and notifications</p>
        </div>

        <div className="step-content" style={{ textAlign: 'left', maxWidth: '500px', margin: '0 auto' }}>
          {isStandalone ? (
            // Already installed
            <div style={{
              padding: '20px',
              background: '#d4edda',
              borderRadius: '8px',
              marginBottom: '20px',
              textAlign: 'center'
            }}>
              <div style={{ fontSize: '48px', marginBottom: '10px' }}>✓</div>
              <p style={{ margin: 0, color: '#155724', fontWeight: '500' }}>
                Cluubz is already installed on your device!
              </p>
            </div>
          ) : isIOS ? (
            // iOS - show manual instructions
            <div>
              <div style={{
                padding: '20px',
                background: '#f8f9fa',
                borderRadius: '8px',
                marginBottom: '20px'
              }}>
                <h3 style={{ marginTop: 0, fontSize: '16px', marginBottom: '15px' }}>
                  To install on iPhone/iPad:
                </h3>
                <ol style={{ paddingLeft: '20px', margin: 0, lineHeight: '1.8' }}>
                  <li>Tap the <strong>Share</strong> button <span style={{ fontSize: '20px' }}>⎋</span> at the bottom of your screen</li>
                  <li>Scroll down and tap <strong>"Add to Home Screen"</strong> <span style={{ fontSize: '20px' }}>➕</span></li>
                  <li>Tap <strong>"Add"</strong> in the top right corner</li>
                </ol>
              </div>
              <div style={{
                padding: '15px',
                background: '#e7f3ff',
                borderRadius: '8px',
                fontSize: '14px',
                color: '#004085'
              }}>
                💡 <strong>Tip:</strong> Once installed, Cluubz will open like a native app with quick access from your home screen!
              </div>
            </div>
          ) : deferredPrompt ? (
            // Android/Chrome - can trigger install
            <div>
              <div style={{
                padding: '20px',
                background: '#f8f9fa',
                borderRadius: '8px',
                marginBottom: '20px'
              }}>
                <h3 style={{ marginTop: 0, fontSize: '16px', marginBottom: '15px' }}>
                  Benefits of installing:
                </h3>
                <ul style={{ paddingLeft: '20px', margin: 0, lineHeight: '1.8' }}>
                  <li>Quick access from your home screen</li>
                  <li>Works like a native app</li>
                  <li>Faster loading times</li>
                  <li>Instant activity notifications</li>
                </ul>
              </div>
              <button
                className="primary-button"
                onClick={handleInstallApp}
                style={{
                  width: '100%',
                  padding: '15px',
                  fontSize: '16px',
                  marginBottom: '12px'
                }}
              >
                ➕ Add to Home Screen
              </button>
            </div>
          ) : (
            // Android/Chrome but no prompt available
            <div style={{
              padding: '20px',
              background: '#f8f9fa',
              borderRadius: '8px',
              marginBottom: '20px',
              textAlign: 'center'
            }}>
              <p style={{ margin: 0, color: '#666' }}>
                To install Cluubz, look for the "Install" or "Add to Home Screen" option in your browser menu.
              </p>
            </div>
          )}
        </div>
      </div>
    );
  };

  const renderWidgetSetup = () => {
    return (
      <div className="onboarding-step">
        <div className="step-header">
          <h2>Add Today's Activities Widget</h2>
          <p>See your children's daily schedule at a glance from your home screen</p>
        </div>

        <div className="step-content" style={{ textAlign: 'left', maxWidth: '500px', margin: '0 auto' }}>
          {/* Widget Preview Mockup */}
          <div style={{
            marginBottom: '24px',
            padding: '20px',
            background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
            borderRadius: '20px',
            boxShadow: '0 8px 32px rgba(102, 126, 234, 0.3)'
          }}>
            <div style={{
              background: 'rgba(255,255,255,0.95)',
              borderRadius: '16px',
              padding: '16px',
              fontFamily: '-apple-system, BlinkMacSystemFont, "SF Pro Display", sans-serif'
            }}>
              <div style={{ display: 'flex', alignItems: 'center', marginBottom: '12px' }}>
                <img
                  src="/cluubz-logo-purple.png"
                  alt="Cluubz"
                  style={{ width: '24px', height: '24px', marginRight: '8px' }}
                  onError={(e) => { e.currentTarget.style.display = 'none'; }}
                />
                <span style={{ fontWeight: '600', color: '#333', fontSize: '14px' }}>Today's Activities</span>
              </div>
              <div style={{ borderTop: '1px solid #eee', paddingTop: '12px' }}>
                <div style={{ marginBottom: '10px' }}>
                  <div style={{ fontSize: '12px', color: '#666', marginBottom: '2px' }}>Emma</div>
                  <div style={{ display: 'flex', alignItems: 'center' }}>
                    <span style={{ fontSize: '13px', fontWeight: '500', color: '#333' }}>Swimming 3:30 PM</span>
                    <span style={{ marginLeft: 'auto', fontSize: '11px', color: '#667eea', background: '#f0f3ff', padding: '2px 8px', borderRadius: '10px' }}>+1 more</span>
                  </div>
                </div>
                <div>
                  <div style={{ fontSize: '12px', color: '#666', marginBottom: '2px' }}>Oliver</div>
                  <div style={{ fontSize: '13px', fontWeight: '500', color: '#333' }}>Football 4:00 PM</div>
                </div>
              </div>
            </div>
            <div style={{ textAlign: 'center', marginTop: '12px', color: 'rgba(255,255,255,0.9)', fontSize: '12px', fontWeight: '500' }}>
              Widget Preview
            </div>
          </div>

          <div style={{
            padding: '20px',
            background: '#f8f9fa',
            borderRadius: '8px',
            marginBottom: '20px'
          }}>
            <h3 style={{ marginTop: 0, fontSize: '16px', marginBottom: '15px' }}>
              To add the widget to your home screen:
            </h3>
            <ol style={{ paddingLeft: '20px', margin: 0, lineHeight: '1.8' }}>
              <li><strong>Long-press</strong> on an empty area of your home screen</li>
              <li>Tap the <strong>"+"</strong> or <strong>"Edit"</strong> button in the top-left corner</li>
              <li>Search for <strong>"Cluubz"</strong> in the widget gallery</li>
              <li>Choose your preferred widget size (Small, Medium, or Large)</li>
              <li>Tap <strong>"Add Widget"</strong></li>
            </ol>
          </div>

          <div style={{
            padding: '15px',
            background: '#e7f3ff',
            borderRadius: '8px',
            fontSize: '14px',
            color: '#004085',
            marginBottom: '20px'
          }}>
            <strong>Widget Sizes:</strong>
            <ul style={{ margin: '10px 0 0 0', paddingLeft: '20px' }}>
              <li><strong>Small:</strong> Shows the next activity name and count of remaining</li>
              <li><strong>Medium:</strong> Shows next activity per child with remaining count</li>
              <li><strong>Large:</strong> Full schedule by child with activity names</li>
            </ul>
          </div>

          <div style={{
            padding: '15px',
            background: '#fff3cd',
            borderRadius: '8px',
            fontSize: '14px',
            color: '#856404'
          }}>
            <strong>Note:</strong> The widget updates automatically every 15 minutes with your children's upcoming activities.
          </div>
        </div>
      </div>
    );
  };

  if (loading) {
    return (
      <div className="onboarding-overlay">
        <div className="onboarding-container">
          <div className="progress-bar">
            <div className="progress-header">
              <div className="progress-logo">
                <img 
                  src="/cluubz-logo-white.png" 
                  alt="Cluubz" 
                  className="progress-brand-logo"
                  onError={(e) => {
                    console.error('Progress logo failed to load:', e);
                    e.currentTarget.style.display = 'none';
                  }}
                />
              </div>
            </div>
          </div>
          <div className="onboarding-content">
            <div className="onboarding-step">
              <div className="step-header">
                <h2>Loading...</h2>
                <p>Checking your account setup...</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="onboarding-overlay">
      <div className="onboarding-container">
        <div className="progress-bar">
          <div className="progress-header">
          <div className="progress-logo">
            <img 
              src="/cluubz-logo-white.png" 
              alt="Cluubz" 
              className="progress-brand-logo"
              onError={(e) => {
                console.error('Progress logo failed to load:', e);
                e.currentTarget.style.display = 'none';
              }}
            />
          </div>
        </div>
        </div>

        <div className="onboarding-content">
          {currentStep === 'welcome' && renderStep1()}
          {currentStep === 'add_children_prompt' && renderAddChildrenPrompt()}
          {currentStep === 'parent_added' && renderParentAddedStep()}
          {currentStep === 'child_setup' && renderChildSetupStep()}
          {currentStep === 'skeleton_dob' && renderSkeletonDOBStep()}
          {currentStep === 'child_app_questionnaire' && renderChildAppQuestionnaire()}
          {currentStep === 'integrations_setup' && renderIntegrationsSetup()}
          {currentStep === 'pitchero_mappings_review' && renderPitcheroMappingsReview()}
          {/* term_planner_intro and term_planner_setup removed — TP flow handled by ProfileScreen */}
          {currentStep === 'connections' && renderConnections()}
          {currentStep === 'add_to_home_screen' && renderAddToHomeScreen()}
          {currentStep === 'widget_setup' && renderWidgetSetup()}
        </div>

        {/* Step Navigation Bar - Hidden on term_planner_setup and child_app_questionnaire (has its own nav) */}
        {currentStep !== 'term_planner_setup' && currentStep !== 'child_app_questionnaire' && (
        <div style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          padding: '12px 20px',
          paddingBottom: 'max(12px, env(safe-area-inset-bottom, 12px))',
          borderTop: '1px solid #e0e0e0',
          backgroundColor: '#f8f9fa',
          marginTop: 'auto'
        }}>
          <button
            onClick={goToPreviousStep}
            disabled={!canGoBack()}
            style={{
              padding: '8px 16px',
              backgroundColor: canGoBack() ? '#6c757d' : '#e0e0e0',
              color: canGoBack() ? 'white' : '#999',
              border: 'none',
              borderRadius: '6px',
              cursor: canGoBack() ? 'pointer' : 'not-allowed',
              fontSize: '14px',
              fontWeight: '500'
            }}
          >
            ← Back
          </button>

          <div style={{
            fontSize: '12px',
            color: '#666',
            textAlign: 'center'
          }}>
            Step {getCurrentStepNumber()} of {STEP_ORDER.length}
            <div style={{ fontSize: '11px', marginTop: '2px' }}>
              {STEP_LABELS[currentStep] || currentStep}
            </div>
          </div>

          {/* Hide Next button on steps that already have an inline Continue button —
              showing both at once confused users. Back stays so users can navigate back. */}
          {(() => {
            const stepHasInlineContinue =
              currentStep === 'welcome' ||
              currentStep === 'add_children_prompt' ||
              currentStep === 'parent_added' ||
              currentStep === 'child_setup' ||
              currentStep === 'skeleton_dob' ||
              (currentStep === 'integrations_setup' && (
                showPitcheroCompletionScreen ||
                showSpondCompletionScreen ||
                showSOCSCompletionScreen ||
                showSchoolPortalCompletionScreen ||
                showSchoolsBuddyCompletionScreen
              ));
            if (stepHasInlineContinue) {
              // Invisible spacer keeps the step indicator centered.
              return <div style={{ width: '80px' }} />;
            }
            // child_setup and add_children_prompt always have inline Continue, so
            // they never render this button — the disabled check no longer needs
            // to consider them.
            const nextDisabled = connectingIntegration || savingMappings;
            return (
              <button
                onClick={goToNextStep}
                disabled={nextDisabled}
                style={{
                  padding: '8px 16px',
                  backgroundColor: nextDisabled ? '#6c757d' : '#007bff',
                  color: 'white',
                  border: 'none',
                  borderRadius: '6px',
                  cursor: nextDisabled ? 'not-allowed' : 'pointer',
                  fontSize: '14px',
                  fontWeight: '500',
                  opacity: nextDisabled ? 0.7 : 1
                }}
              >
                {connectingIntegration ? 'Connecting...' : savingMappings ? 'Importing...' : canGoForward() ? 'Next →' : 'Finish'}
              </button>
            );
          })()}
        </div>
        )}
      </div>

      {/* Pitchero Setup Modal */}
      <PitcheroSetupModal
        isOpen={showPitcheroModal && !!primaryParentUuid}
        onClose={handlePitcheroModalClose}
        parentUuid={primaryParentUuid || ''}
        suggestedEmails={[...allParents.map(p => p.email).filter(Boolean), ...customEmails]}
        defaultEmail={userInfo?.email || ''}
        onEmailUsed={(email) => setCustomEmails(prev => prev.includes(email) ? prev : [...prev, email])}
      />

      {/* Spond Setup Modal */}
      <SpondSetupModal
        isOpen={showSpondModal}
        onClose={(completed) => {
          setShowSpondModal(false);
          if (completed) {
            setSpondCompleted(true);
            // Always show completion screen during onboarding if there are more integrations
            if (currentStep === 'integrations_setup') {
              setShowSpondCompletionScreen(true);
            }
          } else if (currentStep === 'integrations_setup') {
            setCurrentStep('child_app_questionnaire');
          }
        }}
        children={getChildrenForApp('spond').length > 0 ? getChildrenForApp('spond') : allChildren.filter((c: any) => !c.is_parent)}
        suggestedEmails={[...allParents.map(p => p.email).filter(Boolean), ...customEmails]}
        defaultEmail={userInfo?.email || ''}
        onEmailUsed={(email) => setCustomEmails(prev => prev.includes(email) ? prev : [...prev, email])}
      />

      {/* SOCS Setup Modal */}
      <SOCSSetupModal
        isOpen={showSOCSModal}
        onClose={handleSOCSModalClose}
        children={childrenForSOCS}
      />

      {/* School Portal Setup Modal */}
      <SchoolPortalSetupModal
        isOpen={showSchoolPortalModal}
        onClose={handleSchoolPortalModalClose}
        children={getChildrenForApp('schoolportal').length > 0 ? getChildrenForApp('schoolportal') : allChildren.filter((c: any) => !c.is_parent)}
        suggestedEmails={[...allParents.map(p => p.email).filter(Boolean), ...customEmails]}
        defaultEmail={userInfo?.email || ''}
        onEmailUsed={(email) => setCustomEmails(prev => prev.includes(email) ? prev : [...prev, email])}
        clubsEnabledChildUuids={Object.entries(childAppSelections)
          .filter(([_, sel]) => sel.clubsAndExtraCurricular.includes('schoolportal'))
          .map(([childUuid]) => childUuid)}
      />

      {/* SchoolsBuddy Setup Modal - pass one child at a time */}
      <SchoolsBuddySetupModal
        isOpen={showSchoolsBuddyModal}
        onClose={handleSchoolsBuddyModalClose}
        children={schoolsBuddyChildrenList.length > 0 ? [schoolsBuddyChildrenList[schoolsBuddyChildIndex]] : allChildren.filter((c: any) => !c.is_parent)}
        suggestedEmails={[...allParents.map(p => p.email).filter(Boolean), ...customEmails]}
        defaultEmail={userInfo?.email || ''}
        onEmailUsed={(email) => setCustomEmails(prev => prev.includes(email) ? prev : [...prev, email])}
      />

      {/* Term Planner Club Modal */}
      {showClubModal && (
        <TermPlannerClubModal
          onClubSelect={handleClubSelect}
          onCancel={() => setShowClubModal(false)}
          child={termPlannerChildren.find(c => c.uuid === selectedTermChild) || undefined}
        />
      )}

      {/* Add Child Modal */}
      {showAddChildModal && (
        <div className="modal-overlay" onClick={() => setShowAddChildModal(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()} style={{ maxWidth: '450px' }}>
            <div className="modal-header">
              <h2>Add Child</h2>
              <button
                className="close-btn"
                onClick={() => setShowAddChildModal(false)}
              >
                ×
              </button>
            </div>
            <div className="modal-body">
              <div className="form-group">
                <label>First Name *</label>
                <input
                  type="text"
                  value={newChildFirstName}
                  onChange={(e) => setNewChildFirstName(e.target.value)}
                  className="form-input"
                  placeholder="Enter first name"
                />
              </div>

              <div className="form-group">
                <label>Last Name</label>
                <input
                  type="text"
                  value={newChildLastName}
                  onChange={(e) => setNewChildLastName(e.target.value)}
                  className="form-input"
                  placeholder="Enter last name (optional)"
                />
              </div>

              <div className="form-group">
                <label>Birth Date</label>
                <div style={{ display: 'flex', gap: '8px' }}>
                  <select
                    value={newChildBirthDay}
                    onChange={(e) => setNewChildBirthDay(e.target.value)}
                    className="form-input"
                    style={{ flex: 1 }}
                  >
                    <option value="">Day</option>
                    {Array.from({ length: 31 }, (_, i) => i + 1).map(day => (
                      <option key={day} value={day}>{day}</option>
                    ))}
                  </select>
                  <select
                    value={newChildBirthMonth}
                    onChange={(e) => setNewChildBirthMonth(e.target.value)}
                    className="form-input"
                    style={{ flex: 1.5 }}
                  >
                    <option value="">Month</option>
                    <option value="1">January</option>
                    <option value="2">February</option>
                    <option value="3">March</option>
                    <option value="4">April</option>
                    <option value="5">May</option>
                    <option value="6">June</option>
                    <option value="7">July</option>
                    <option value="8">August</option>
                    <option value="9">September</option>
                    <option value="10">October</option>
                    <option value="11">November</option>
                    <option value="12">December</option>
                  </select>
                  <select
                    value={newChildBirthYear}
                    onChange={(e) => setNewChildBirthYear(e.target.value)}
                    className="form-input"
                    style={{ flex: 1 }}
                  >
                    <option value="">Year</option>
                    {Array.from({ length: 19 }, (_, i) => new Date().getFullYear() - i).map(year => (
                      <option key={year} value={year}>{year}</option>
                    ))}
                  </select>
                </div>
              </div>

              <div className="form-group">
                <label>School Name *</label>
                <input
                  type="text"
                  value={newChildSchoolVenue}
                  onChange={(e) => setNewChildSchoolVenue(e.target.value)}
                  className="form-input"
                  placeholder="e.g., St Mary's School"
                />
              </div>
            </div>
            <div className="modal-footer">
              <button
                onClick={() => setShowAddChildModal(false)}
                className="cancel-btn"
              >
                Cancel
              </button>
              <button
                onClick={handleAddChild}
                disabled={savingChild}
                className="save-btn"
              >
                {savingChild ? 'Adding...' : 'Add Child'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Add Parent Modal */}
      {showAddParentModal && (
        <div className="modal-overlay" onClick={() => setShowAddParentModal(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()} style={{ maxWidth: '450px' }}>
            <div className="modal-header">
              <h2>Add Parent to Account</h2>
              <button
                className="close-btn"
                onClick={() => setShowAddParentModal(false)}
              >
                ×
              </button>
            </div>
            <div className="modal-body">
              <div style={{ display: 'flex', gap: '12px' }}>
                <div className="form-group" style={{ flex: 1 }}>
                  <label>First Name *</label>
                  <input
                    type="text"
                    value={newParentFirstName}
                    onChange={(e) => setNewParentFirstName(e.target.value)}
                    className="form-input"
                    placeholder="First name"
                  />
                </div>
                <div className="form-group" style={{ flex: 1 }}>
                  <label>Last Name *</label>
                  <input
                    type="text"
                    value={newParentLastName}
                    onChange={(e) => setNewParentLastName(e.target.value)}
                    className="form-input"
                    placeholder="Last name"
                  />
                </div>
              </div>

              <div className="form-group">
                <label>Email *</label>
                <input
                  type="email"
                  value={newParentEmail}
                  onChange={(e) => setNewParentEmail(e.target.value)}
                  className="form-input"
                  placeholder="Enter email address"
                />
              </div>

              <div className="form-group">
                <label>Phone *</label>
                <input
                  type="tel"
                  value={newParentPhone}
                  onChange={(e) => setNewParentPhone(e.target.value)}
                  className="form-input"
                  placeholder="Enter phone number"
                />
              </div>

              <div className="form-group">
                <label>Password *</label>
                <div style={{ position: 'relative' }}>
                  <input
                    type={showParentPassword ? "text" : "password"}
                    value={newParentPassword}
                    onChange={(e) => setNewParentPassword(e.target.value)}
                    className="form-input"
                    placeholder="Min 6 characters"
                    style={{ paddingRight: '40px' }}
                  />
                  <button
                    type="button"
                    onClick={() => setShowParentPassword(!showParentPassword)}
                    style={{
                      position: 'absolute',
                      right: '10px',
                      top: '50%',
                      transform: 'translateY(-50%)',
                      background: 'none',
                      border: 'none',
                      cursor: 'pointer'
                    }}
                  >
                    {showParentPassword ? '🙈' : '👁️'}
                  </button>
                </div>
              </div>

              <div className="form-group">
                <label>Confirm Password *</label>
                <div style={{ position: 'relative' }}>
                  <input
                    type={showParentConfirmPassword ? "text" : "password"}
                    value={newParentConfirmPassword}
                    onChange={(e) => setNewParentConfirmPassword(e.target.value)}
                    className="form-input"
                    placeholder="Confirm password"
                    style={{ paddingRight: '40px' }}
                  />
                  <button
                    type="button"
                    onClick={() => setShowParentConfirmPassword(!showParentConfirmPassword)}
                    style={{
                      position: 'absolute',
                      right: '10px',
                      top: '50%',
                      transform: 'translateY(-50%)',
                      background: 'none',
                      border: 'none',
                      cursor: 'pointer'
                    }}
                  >
                    {showParentConfirmPassword ? '🙈' : '👁️'}
                  </button>
                </div>
              </div>

              <div className="form-group">
                <label>Role</label>
                <select
                  value={newParentRole}
                  onChange={(e) => setNewParentRole(e.target.value)}
                  className="form-input"
                >
                  <option value="parent">Parent</option>
                  <option value="guardian">Guardian</option>
                  <option value="caregiver">Caregiver</option>
                </select>
              </div>
            </div>
            <div className="modal-footer">
              <button
                onClick={() => setShowAddParentModal(false)}
                className="cancel-btn"
                disabled={addingParent}
              >
                Cancel
              </button>
              <button
                onClick={handleCreateParent}
                disabled={addingParent}
                className="save-btn"
              >
                {addingParent ? 'Adding...' : 'Add Parent'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default OnboardingFlow;